import * as THREE from 'three';
import { createTextCanvasTexture } from './text-canvas-texture';
import {
  configureNormalMapSampling,
  configureStableTextureSampling,
  cloneTextureLight,
  commitTextureUV,
} from './texture-sampling';
import {
  applyPlayerTextureDownscale,
  getPlayerTextureQualityOptions,
  getTextureQualityCacheSuffix,
  optimizeTextureUrl,
  registerPlayerTextureCacheClearHook,
} from './player-texture-quality';
import {
  registerAssetUrlTextureClearHook,
  disposeTexturesInMapByUrl,
} from './asset-url-invalidation';

/**
 * Max long-edge for CPU graph bakes. Must match the texture-quality cap so
 * Exposure / HSL / Blend do not silently downsample 4k maps.
 */
function graphCpuTextureSizeCap(): number {
  const opts = getPlayerTextureQualityOptions();
  if (typeof opts.cpuBakeMaxEdge === 'number' && opts.cpuBakeMaxEdge > 0) {
    return Math.max(64, Math.floor(opts.cpuBakeMaxEdge));
  }
  if (opts.resolutionScale <= 0.25) return 256;
  if (opts.resolutionScale <= 0.5) return 512;
  switch (opts.maxEdge) {
    case '1024':
      return 1024;
    case '2048':
      return 2048;
    case '4096':
      return 4096;
    default:
      return 4096;
  }
}

/**
 * Engraving Text Canvas budget. Authored 1024×ss3 peaks ~37MB RGBA per output (× bump/opacity).
 * With wood + CJK (~12MB OTF) that OOMs iPad Safari — keep mobile canvases small / no supersample.
 */
function textCanvasDeviceBudget(): { maxEdge: number; maxSuperSample: number } {
  const opts = getPlayerTextureQualityOptions();
  if (opts.cpuBakeMaxEdge == null) {
    return { maxEdge: 4096, maxSuperSample: 4 };
  }
  if (opts.cpuBakeMaxEdge <= 512) {
    return { maxEdge: 512, maxSuperSample: 1 };
  }
  // Tablet: keep output size but drop supersample (no 3072² scratch).
  return { maxEdge: 1024, maxSuperSample: 1 };
}

function graphCpuTextureSize(): number {
  return graphCpuTextureSizeCap();
}

type CpuBakeDims = { w: number; h: number };

function fitCpuBakeDims(w: number, h: number, cap: number): CpuBakeDims {
  if (!Number.isFinite(w) || !Number.isFinite(h) || w < 2 || h < 2) {
    const f = Math.min(1024, cap);
    return { w: f, h: f };
  }
  const long = Math.max(w, h);
  const scale = long > cap ? cap / long : 1;
  return {
    w: Math.max(64, Math.round(w * scale)),
    h: Math.max(64, Math.round(h * scale)),
  };
}

/** Source pixel size, uniformly scaled to fit the quality cap (keeps aspect — no square stretch). */
function graphCpuBakeDimsFor(tex?: THREE.Texture | null): CpuBakeDims {
  const cap = graphCpuTextureSizeCap();
  if (!tex) {
    const f = Math.min(1024, cap);
    return { w: f, h: f };
  }
  adoptCloneSourceImage(tex);
  if (!isImageReady(tex.image)) return { w: cap, h: cap };
  const dim = getCanvasImageDimensions(tex.image as CanvasImageSource);
  return fitCpuBakeDims(dim.w, dim.h, cap);
}

function graphCpuBakeDimsForMany(texes: Array<THREE.Texture | null | undefined>): CpuBakeDims {
  let best: CpuBakeDims | null = null;
  let bestArea = 0;
  for (const t of texes) {
    if (!t) continue;
    const d = graphCpuBakeDimsFor(t);
    const a = d.w * d.h;
    if (a > bestArea) {
      best = d;
      bestArea = a;
    }
  }
  if (best) return best;
  const cap = graphCpuTextureSizeCap();
  return { w: cap, h: cap };
}

function graphCpuBakeSizeFor(tex?: THREE.Texture | null): number {
  const d = graphCpuBakeDimsFor(tex);
  return Math.max(d.w, d.h);
}

function ensureCpuBakeCanvasSize(
  canvas: HTMLCanvasElement,
  w: number,
  h: number,
): CanvasRenderingContext2D | null {
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  return canvas.getContext('2d', { willReadFrequently: true });
}

/** Guard against THREE "Texture marked for update but no image data" when the source hasn't loaded yet. */
function safeMarkNeedsUpdate(tex: THREE.Texture): void {
  if ((tex as THREE.DataTexture).isDataTexture === true || (tex as THREE.CanvasTexture).isCanvasTexture === true) {
    tex.needsUpdate = true;
    return;
  }
  const im = tex.image as { complete?: boolean; naturalWidth?: number; width?: number; height?: number } | null | undefined;
  if (im == null) return;
  if (typeof (im as HTMLImageElement).complete === 'boolean') {
    if ((im as HTMLImageElement).complete && ((im as HTMLImageElement).naturalWidth ?? 0) > 0) tex.needsUpdate = true;
    return;
  }
  if (typeof im.width === 'number' && im.width > 0) tex.needsUpdate = true;
}

export interface MaterialGraphNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: Record<string, any>;
}

export interface MaterialGraphEdge {
  id: string;
  source: string;
  sourceHandle: string;
  target: string;
  targetHandle: string;
}

export interface MaterialGraphData {
  nodes: MaterialGraphNode[];
  edges: MaterialGraphEdge[];
  meshTargets: string[];
  tagTargets?: string[];
  /** PBR Output or Material Blender node id used as the graph exit. */
  activeOutputId?: string;
  /** Intermediate node id whose output is forced onto the mesh (editor debug). */
  debugPreviewNodeId?: string;
  /** Editor UV debug scope: `assigned` (default) targets this material's meshes; `all` = every displayed element. */
  debugPreviewScope?: 'assigned' | 'all';
}

export interface EvalResult {
  overrides: Record<string, any>;
  textures: THREE.Texture[];
}

/** Optional evaluation context (e.g. mesh name for per-element UV randomization). */
export interface MaterialGraphEvalContext {
  /** When UV Randomizer has "per mesh" on, this string is hashed into the seed (mesh name, `*`, tag key, etc.). */
  meshName?: string | null;
  /** Editor only: honor `debugPreviewNodeId` and show intermediate node output on the mesh. */
  allowDebugPreview?: boolean;
  /** Library material name — Gradient skips renderer ACES; other materials stay tone-mapped. */
  libraryName?: string | null;
}

/** Library graphs named Gradient keep authored albedo (ACES washes the gradient map). */
export function libraryMaterialSkipsToneMap(name: string | undefined | null): boolean {
  return typeof name === 'string' && /gradient/i.test(name);
}

export function stampSkipToneMapFromLibraryName(
  overrides: Record<string, any> | null | undefined,
  libraryName?: string | null,
): void {
  if (!overrides) return;
  if (overrides.skipToneMap === true || overrides.skipToneMap === false) return;
  if (libraryMaterialSkipsToneMap(libraryName)) overrides.skipToneMap = true;
}

/** True if the graph has any UV Randomizer node with per-mesh variation enabled. */
export function materialGraphUsesPerMeshUvRandomize(graph: MaterialGraphData): boolean {
  return graph.nodes.some((n) => n.type === 'uvRandomize' && !!(n.data as Record<string, unknown>)?.perMesh);
}

/** Stamped on textures leaving a UV Randomizer so array instancing can vary UV per copy. */
export type C3dUvRandomizeMeta = {
  perMesh: boolean;
  seed: number;
  offsetMinU: number;
  offsetMaxU: number;
  offsetStepU: number;
  offsetMinV: number;
  offsetMaxV: number;
  offsetStepV: number;
  rotationMinDeg: number;
  rotationMaxDeg: number;
  rotationStepDeg: number;
  scaleMin: number;
  scaleMax: number;
  scaleStep: number;
  /** 50% chance per roll to flip U around UV center. */
  mirrorU: boolean;
  /** 50% chance per roll to flip V around UV center. */
  mirrorV: boolean;
};

export const C3D_UV_RANDOMIZE_UD = '__c3dUvRandomize';

export function uvRandomizeMetaHasVariance(meta: C3dUvRandomizeMeta): boolean {
  return (
    meta.offsetMinU !== meta.offsetMaxU ||
    meta.offsetMinV !== meta.offsetMaxV ||
    meta.rotationMinDeg !== meta.rotationMaxDeg ||
    meta.scaleMin !== meta.scaleMax ||
    meta.mirrorU ||
    meta.mirrorV
  );
}

/** Read UV Randomizer ranges stamped on the material or its maps (set during graph eval). */
export function extractUvRandomizeMetaFromMaterial(
  mat: THREE.Material | THREE.Material[],
): C3dUvRandomizeMeta | null {
  const mats = Array.isArray(mat) ? mat : [mat];
  for (const m of mats) {
    const onMat = m.userData?.[C3D_UV_RANDOMIZE_UD] as C3dUvRandomizeMeta | undefined;
    if (onMat && typeof onMat === 'object') {
      return { ...onMat, mirrorU: !!onMat.mirrorU, mirrorV: !!onMat.mirrorV };
    }
  }
  const mapKeys = [
    'map',
    'normalMap',
    'roughnessMap',
    'metalnessMap',
    'aoMap',
    'emissiveMap',
    'alphaMap',
    'bumpMap',
    'displacementMap',
    'clearcoatMap',
    'clearcoatNormalMap',
    'clearcoatRoughnessMap',
    'sheenColorMap',
    'sheenRoughnessMap',
    'specularColorMap',
    'specularIntensityMap',
    'transmissionMap',
    'thicknessMap',
    'anisotropyMap',
  ] as const;
  for (const m of mats) {
    const anyMat = m as unknown as Record<string, THREE.Texture | null | undefined>;
    for (const key of mapKeys) {
      const tex = anyMat[key];
      const meta = tex?.userData?.[C3D_UV_RANDOMIZE_UD] as C3dUvRandomizeMeta | undefined;
      if (meta && typeof meta === 'object') {
        return {
          ...meta,
          mirrorU: !!meta.mirrorU,
          mirrorV: !!meta.mirrorV,
        };
      }
    }
  }
  return null;
}

/** Build instance-UV meta from node data alone (no texture decode required). */
export function buildUvRandomizeMetaFromNodeData(
  d: Record<string, unknown>,
  perMesh: boolean,
  baseSeed: number,
): C3dUvRandomizeMeta {
  const minU = typeof d.offsetMinU === 'number' ? d.offsetMinU : 0;
  const maxU = typeof d.offsetMaxU === 'number' ? d.offsetMaxU : 0;
  const minV = typeof d.offsetMinV === 'number' ? d.offsetMinV : 0;
  const maxV = typeof d.offsetMaxV === 'number' ? d.offsetMaxV : 0;
  const loU = Math.min(minU, maxU);
  const hiU = Math.max(minU, maxU);
  const loV = Math.min(minV, maxV);
  const hiV = Math.max(minV, maxV);
  const rotMin = typeof d.rotationMinDeg === 'number' ? d.rotationMinDeg : 0;
  const rotMax = typeof d.rotationMaxDeg === 'number' ? d.rotationMaxDeg : 0;
  const loR = Math.min(rotMin, rotMax);
  const hiR = Math.max(rotMin, rotMax);
  const rmiX = d.repeatMinX;
  const rmaX = d.repeatMaxX;
  const rmiY = d.repeatMinY;
  const rmaY = d.repeatMaxY;
  const scaleJitter = Math.min(1, Math.max(0, Number(d.scaleJitter) || 0));
  let scaleMin = 1;
  let scaleMax = 1;
  let scaleStep = 0;
  const repeatStepX =
    typeof d.repeatStepX === 'number' && d.repeatStepX > 0 ? d.repeatStepX : 0;
  const repeatStepY =
    typeof d.repeatStepY === 'number' && d.repeatStepY > 0 ? d.repeatStepY : 0;
  if (typeof rmiX === 'number' && typeof rmaX === 'number') {
    scaleMin = Math.min(rmiX, rmaX);
    scaleMax = Math.max(rmiX, rmaX);
    scaleStep = repeatStepX;
  } else if (typeof rmiY === 'number' && typeof rmaY === 'number') {
    scaleMin = Math.min(rmiY, rmaY);
    scaleMax = Math.max(rmiY, rmaY);
    scaleStep = repeatStepY;
  } else if (scaleJitter > 0) {
    scaleMin = Math.max(0.01, 1 - scaleJitter);
    scaleMax = 1 + scaleJitter;
  }
  return {
    perMesh,
    seed: baseSeed,
    offsetMinU: loU,
    offsetMaxU: hiU,
    offsetStepU: typeof d.offsetStepU === 'number' && d.offsetStepU > 0 ? d.offsetStepU : 0,
    offsetMinV: loV,
    offsetMaxV: hiV,
    offsetStepV: typeof d.offsetStepV === 'number' && d.offsetStepV > 0 ? d.offsetStepV : 0,
    rotationMinDeg: loR,
    rotationMaxDeg: hiR,
    rotationStepDeg: typeof d.rotationStepDeg === 'number' && d.rotationStepDeg > 0 ? d.rotationStepDeg : 0,
    scaleMin,
    scaleMax,
    scaleStep,
    mirrorU: !!d.mirrorU,
    mirrorV: !!d.mirrorV,
  };
}

/** When step > 0, pick uniformly among a, a+step, …, ≤ b (stepped random, similar to Corona From/To/Step). */
export function randomInRangeWithStep(a: number, b: number, step: number, rand: () => number): number {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  if (!(step > 0) || !Number.isFinite(step)) {
    return lo + rand() * (hi - lo);
  }
  const span = hi - lo;
  if (span <= 0) return lo;
  const n = Math.min(4096, Math.floor(span / step + 1e-12) + 1);
  const k = Math.min(n - 1, Math.floor(rand() * n));
  return lo + k * step;
}

const PBR_SLOT_DEFAULTS: Record<string, number | string> = {
  color: '#ffffff',
  bumpScale: 1,
  roughness: 0.5,
  metalness: 0,
  opacity: 1,
  transmission: 0,
  ior: 1.5,
  thickness: 0,
  attenuationColor: '#ffffff',
  attenuationDistance: 0,
  clearcoat: 0,
  clearcoatRoughness: 0,
  sheenColor: '#000000',
  sheenRoughness: 0,
  specularIntensity: 1,
  specularColor: '#ffffff',
  envMapIntensity: 1,
  anisotropy: 0,
  anisotropyRotation: 0,
  emissive: '#000000',
  emissiveIntensity: 1,
};

/**
 * Graph → player contract: coat / sheen / transmission must always be present.
 * If a PBR Output never wired Clearcoat, older eval omitted the keys and the
 * reused MeshPhysicalMaterial kept Carpaint (or any prior) lacquer.
 */
function ensureExplicitCoatFields(result: Record<string, any>): void {
  if (typeof result.clearcoat !== 'number' || !Number.isFinite(result.clearcoat)) {
    result.clearcoat = 0;
  }
  if (typeof result.clearcoatRoughness !== 'number' || !Number.isFinite(result.clearcoatRoughness)) {
    result.clearcoatRoughness = 0;
  }
  if (!(result.clearcoatMap instanceof THREE.Texture)) result.clearcoatMap = null;
  if (!(result.clearcoatRoughnessMap instanceof THREE.Texture)) result.clearcoatRoughnessMap = null;
  if (!(result.clearcoatNormalMap instanceof THREE.Texture)) result.clearcoatNormalMap = null;
  if (typeof result.transmission !== 'number' || !Number.isFinite(result.transmission)) {
    result.transmission = 0;
  }
  if (typeof result.sheenColor !== 'string' || !result.sheenColor) {
    result.sheenColor = '#000000';
  }
  if (typeof result.sheenRoughness !== 'number' || !Number.isFinite(result.sheenRoughness)) {
    result.sheenRoughness = 0;
  }
  // Same contract as coat: omitted anisotropy left Carbon Fiber / brushed metal on Wood.
  // A texture wired to the Anisotropy *number* port used to land here as a Texture object,
  // get coerced to 0, and disable USE_ANISOTROPY — wiping Aniso Rot / Aniso Map entirely.
  if (typeof result.anisotropy !== 'number' || !Number.isFinite(result.anisotropy)) {
    result.anisotropy = result.anisotropyMap instanceof THREE.Texture ? 1 : 0;
  }
  if (typeof result.anisotropyRotation !== 'number' || !Number.isFinite(result.anisotropyRotation)) {
    result.anisotropyRotation = 0;
  }
  if (!(result.anisotropyMap instanceof THREE.Texture)) result.anisotropyMap = null;
}

const texLoader = new THREE.TextureLoader();
const texCache = new Map<string, THREE.Texture>();

/** TextureLoader fetches still in flight (first paint / luxury reveal gate). */
let pendingGraphImageLoads = 0;
/** CPU chunked graph processors (bright/contrast, invert, blur, …) still writing canvases. */
let activeChunkedDraws = 0;

function bumpPendingGraphImageLoads(delta: number): void {
  pendingGraphImageLoads = Math.max(0, pendingGraphImageLoads + delta);
  if (pendingGraphImageLoads === 0 && activeChunkedDraws === 0) {
    dispatchGraphTexturesIdle();
  }
}

function bumpActiveChunkedDraws(delta: number): void {
  activeChunkedDraws = Math.max(0, activeChunkedDraws + delta);
  if (pendingGraphImageLoads === 0 && activeChunkedDraws === 0) {
    dispatchGraphTexturesIdle();
  }
}

function dispatchGraphTexturesIdle(): void {
  if (typeof document === 'undefined') return;
  document.dispatchEvent(new CustomEvent('scene-graph-textures-idle'));
}

/** Pending image decodes that block first reveal. */
export function getPendingMaterialTextureWork(): number {
  return pendingGraphImageLoads;
}

/** In-flight CPU graph processors (AO / bright-contrast / invert / …). */
export function getActiveGraphTextureJobs(): number {
  return activeChunkedDraws;
}

const LINEAR_SLOTS = new Set([
  'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
  'aoMap', 'displacementMap', 'alphaMap', 'anisotropyMap',
  'clearcoatMap', 'clearcoatRoughnessMap', 'clearcoatNormalMap',
]);

/** 1×1 white — Material Blender uses this when a layer has a PBR wired but no mask (full coverage × amount). Not disposed with graph results. */
let fullMaskTex: THREE.DataTexture | null = null;
function getFullMaskTexture(): THREE.DataTexture {
  if (!fullMaskTex) {
    const data = new Uint8Array([255, 255, 255, 255]);
    fullMaskTex = new THREE.DataTexture(data, 1, 1, THREE.RGBAFormat);
    fullMaskTex.wrapS = fullMaskTex.wrapT = THREE.ClampToEdgeWrapping;
    fullMaskTex.needsUpdate = true;
  }
  return fullMaskTex;
}

function loadTex(url: string, linear = false, opts?: { track?: boolean }): THREE.Texture {
  const qSuffix = getTextureQualityCacheSuffix();
  const cacheKey = `${url}__${linear ? 'L' : 'S'}__${qSuffix}`;
  if (texCache.has(cacheKey)) return texCache.get(cacheKey)!;
  const fetchUrl = optimizeTextureUrl(url);
  const track = opts?.track !== false;
  if (track) bumpPendingGraphImageLoads(1);
  const tex = texLoader.load(
    fetchUrl,
    (loaded) => {
      applyPlayerTextureDownscale(loaded);
      configureStableTextureSampling(loaded);
      if (track) {
        bumpPendingGraphImageLoads(-1);
        if (typeof document !== 'undefined') {
          document.dispatchEvent(new CustomEvent('scene-texture-ready'));
        }
      }
    },
    undefined,
    () => {
      if (track) bumpPendingGraphImageLoads(-1);
    },
  );
  tex.colorSpace = linear ? THREE.LinearSRGBColorSpace : THREE.SRGBColorSpace;
  tex.flipY = false;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  texCache.set(cacheKey, tex);
  return tex;
}

function nodeMuted(node: MaterialGraphNode): boolean {
  return !!(node.data as Record<string, unknown>).muted;
}

function nodeBranchDisabled(node: MaterialGraphNode): boolean {
  return !!(node.data as Record<string, unknown>).disabled;
}

function isGraphExitNode(node: MaterialGraphNode | undefined): node is MaterialGraphNode {
  return node?.type === 'pbrOutput' || node?.type === 'materialBlender';
}

function topoSort(nodes: MaterialGraphNode[], edges: MaterialGraphEdge[]): string[] {
  const inDeg = new Map<string, number>();
  const adj = new Map<string, string[]>();
  for (const n of nodes) {
    inDeg.set(n.id, 0);
    adj.set(n.id, []);
  }
  for (const e of edges) {
    inDeg.set(e.target, (inDeg.get(e.target) ?? 0) + 1);
    adj.get(e.source)?.push(e.target);
  }
  const queue: string[] = [];
  for (const [id, deg] of inDeg) {
    if (deg === 0) queue.push(id);
  }
  const order: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    order.push(id);
    for (const next of adj.get(id) ?? []) {
      const d = (inDeg.get(next) ?? 1) - 1;
      inDeg.set(next, d);
      if (d === 0) queue.push(next);
    }
  }
  return order;
}

export interface AttributeDef {
  key: string;
  type: string;
  /** When selections[key] is missing, use this (option key for single_select). */
  defaultValue?: string | null;
  options?: Array<{ key: string; label: string; color?: string }>;
}

/** Per-option font override on Text Canvas (`fontMap`). */
export type TextCanvasFontMapEntry = {
  fontFamily?: string;
  fontUrl?: string | null;
  fontSize?: number;
  /** Max user-input graphemes for this key (applied before Script). */
  maxChars?: number;
};

/** Truncate by grapheme / code-point count (CJK-safe). */
export function truncateGraphemes(text: string, maxChars: number): string {
  if (!Number.isFinite(maxChars) || maxChars < 0) return text;
  if (maxChars === 0) return '';
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    try {
      const seg = new (Intl as typeof Intl & {
        Segmenter: new (
          locales?: string | string[],
          options?: { granularity?: string },
        ) => { segment: (s: string) => Iterable<{ segment: string }> };
      }).Segmenter(undefined, { granularity: 'grapheme' });
      const parts = [...seg.segment(text)].map((p) => p.segment);
      if (parts.length <= maxChars) return text;
      return parts.slice(0, maxChars).join('');
    } catch {
      /* fall through */
    }
  }
  const parts = [...text];
  return parts.length <= maxChars ? text : parts.slice(0, maxChars).join('');
}

function selectionTruthyForScript(value: unknown): boolean {
  if (value === true || value === 'true') return true;
  if (typeof value === 'string') {
    const s = value.toLowerCase().trim();
    return s !== '' && !['0', 'false', 'off', 'no', 'disabled'].includes(s);
  }
  return !!value;
}

/**
 * Resolve a fontMap entry for Font Type key (+ optional Engraving Dots on/off).
 * Lookup: `ch:on` → `ch_on` → `ch`.
 */
export function resolveTextCanvasFontMapEntry(
  fontMap: Record<string, TextCanvasFontMapEntry> | undefined,
  fontKey: string | null | undefined,
  scriptWired: boolean,
  scriptOn: boolean,
): TextCanvasFontMapEntry | undefined {
  if (!fontMap || fontKey == null || String(fontKey).trim() === '') return undefined;
  const key = String(fontKey).trim();
  const dotsSuffix = scriptOn ? 'on' : 'off';
  const candidates = scriptWired
    ? [`${key}:${dotsSuffix}`, `${key}_${dotsSuffix}`, key]
    : [key];
  for (const cand of candidates) {
    const mapped =
      fontMap[cand] ??
      Object.entries(fontMap).find(([k]) => k.toLowerCase() === cand.toLowerCase())?.[1];
    if (
      mapped &&
      (mapped.fontFamily ||
        mapped.fontUrl ||
        mapped.fontSize != null ||
        (typeof mapped.maxChars === 'number' && Number.isFinite(mapped.maxChars) && mapped.maxChars > 0))
    ) {
      return mapped;
    }
  }
  return undefined;
}

/**
 * Scan graph materials for Text Canvas `fontMap.maxChars` matching current selections.
 * Returns `{ [textAttributeKey]: maxChars }` (tightest limit per text attr).
 */
export function resolveTextAttrMaxCharsFromMaterials(
  materials: Array<{ type?: string; data?: MaterialGraphData | null } | null | undefined>,
  selections: Record<string, unknown>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const m of materials) {
    if (!m || m.type !== 'graph' || !Array.isArray(m.data?.nodes)) continue;
    const graph = m.data as MaterialGraphData;
    for (const node of graph.nodes) {
      if (node.type !== 'textCanvas') continue;
      const fontMap = node.data?.fontMap as Record<string, TextCanvasFontMapEntry> | undefined;
      if (!fontMap) continue;
      if (!Object.values(fontMap).some((e) => typeof e?.maxChars === 'number' && Number.isFinite(e.maxChars) && e.maxChars > 0)) {
        continue;
      }

      let fontAttrKey: string | undefined;
      let scriptAttrKey: string | undefined;
      let textAttrKey: string | undefined;
      for (const e of graph.edges) {
        if (e.target !== node.id) continue;
        const src = graph.nodes.find((n) => n.id === e.source);
        if (src?.type !== 'attributeInput') continue;
        const attrKey = src.data?.attributeKey as string | undefined;
        if (!attrKey) continue;
        if (e.targetHandle === 'font') fontAttrKey = attrKey;
        if (e.targetHandle === 'scriptEnabled') scriptAttrKey = attrKey;
        if (e.targetHandle === 'text') textAttrKey = attrKey;
      }
      if (!textAttrKey) continue;

      const fontKey = fontAttrKey != null ? String(selections[fontAttrKey] ?? '') : '';
      const scriptWired = !!scriptAttrKey;
      const scriptOn = !scriptWired || selectionTruthyForScript(selections[scriptAttrKey!]);
      const entry = resolveTextCanvasFontMapEntry(
        fontMap,
        fontKey || undefined,
        scriptWired,
        scriptOn,
      );
      const n = entry?.maxChars;
      if (typeof n === 'number' && Number.isFinite(n) && n > 0) {
        const floored = Math.floor(n);
        out[textAttrKey] =
          out[textAttrKey] == null ? floored : Math.min(out[textAttrKey]!, floored);
      }
    }
  }
  return out;
}

/** @deprecated Prefer resolveTextAttrMaxCharsFromMaterials; kept for Engraving-named products. */
export function resolveEngravingMaxCharsFromMaterials(
  materials: Array<{ type?: string; data?: MaterialGraphData | null } | null | undefined>,
  selections: Record<string, unknown>,
): number | undefined {
  const map = resolveTextAttrMaxCharsFromMaterials(materials, selections);
  return map.engraving;
}

/** Resolve catalog swatch for an attribute (current selection, or a specific option key). */
function resolveAttributeOptionColor(
  attrKey: string,
  selections: Record<string, unknown>,
  attributeDefs: AttributeDef[] | undefined,
  explicitOptionKey?: string,
): string | null {
  if (!attrKey || !attributeDefs?.length) return null;
  const attrDef = attributeDefs.find((a) => a.key === attrKey);
  let raw: unknown = explicitOptionKey !== undefined ? explicitOptionKey : selections[attrKey];
  if (
    (raw === undefined || raw === null || raw === '') &&
    attrDef?.defaultValue != null &&
    String(attrDef.defaultValue).length > 0
  ) {
    raw = attrDef.defaultValue;
  }
  if (raw == null || raw === '') return null;
  const opt = attrDef?.options?.find((o) => o.key === String(raw));
  return opt?.color ?? null;
}

function defsKeyForGraph(graph: MaterialGraphData, attributeDefs?: AttributeDef[]): string {
  if (!attributeDefs?.length) return '';
  const usedKeys = new Set<string>();
  for (const node of graph.nodes) {
    if (node.type === 'attributeInput') {
      const k = node.data.attributeKey as string;
      if (k) usedKeys.add(k);
    }
    if (node.type === 'colorToTexture' || node.type === 'colorAdjust') {
      const k = String((node.data.attributeKey as string) || '').trim();
      if (k) usedKeys.add(k);
    }
  }
  if (usedKeys.size === 0) return '';
  return [...usedKeys].sort().map((k) => {
    const d = attributeDefs.find((a) => a.key === k);
    if (!d) return `${k}:`;
    const opts = (d.options ?? []).map((o) => `${o.key}/${o.color ?? ''}`).join(';');
    return `${k}:${d.defaultValue ?? ''}:${opts}`;
  }).join('|');
}

// Cache graph evaluation results (includes mesh context when per-mesh UV randomize is used).
const evalCache = new Map<string, EvalResult>();
/** Configurators combine many attribute values; too-small cache evicts still-needed variants and causes first-pick hitches. */
const MAX_EVAL_CACHE = 384;

function graphCacheId(graph: MaterialGraphData): string {
  const n = graph.nodes;
  const e = graph.edges;
  let h = 0;
  // Must include edge endpoints/handles: rewiring Color→Image with the same
  // edge count must invalidate (otherwise albedo stays on the previous solid color).
  const str = JSON.stringify({
    nodes: n.map((nd) => {
      const d = nd.data && typeof nd.data === 'object' ? { ...(nd.data as Record<string, unknown>) } : nd.data;
      if (d && typeof d === 'object') delete (d as Record<string, unknown>).note;
      return { i: nd.id, t: nd.type, d };
    }),
    edges: e.map((ed) => ({
      i: ed.id,
      s: ed.source,
      sh: ed.sourceHandle,
      t: ed.target,
      th: ed.targetHandle,
    })),
    exit: graph.activeOutputId ?? '',
  });
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) | 0;
  }
  return `${n.length}:${e.length}:${h}`;
}

function relevantSelectionsKey(graph: MaterialGraphData, selections: Record<string, unknown>): string {
  const keys = new Set<string>();
  for (const node of graph.nodes) {
    if (node.type === 'attributeInput') {
      const attrKey = node.data.attributeKey as string;
      if (attrKey) keys.add(attrKey);
    }
    if (node.type === 'colorToTexture' || node.type === 'colorAdjust') {
      const k = String((node.data.attributeKey as string) || '').trim();
      if (k) keys.add(k);
    }
  }
  if (keys.size === 0) return '';
  return [...keys].sort().map((k) => `${k}=${selections[k] ?? ''}`).join('|');
}

export function evaluateMaterialGraph(
  graph: MaterialGraphData,
  selections: Record<string, unknown>,
  attributeDefs?: AttributeDef[],
  evalContext?: MaterialGraphEvalContext,
): EvalResult {
  const gId = graphCacheId(graph);
  const selKey = relevantSelectionsKey(graph, selections);
  const defsKey = defsKeyForGraph(graph, attributeDefs);
  const meshSlot = materialGraphUsesPerMeshUvRandomize(graph)
    ? String(evalContext?.meshName ?? '')
    : '*';
  const debugSlot = evalContext?.allowDebugPreview ? (graph.debugPreviewNodeId ?? '') : '';
  const libSlot = String(evalContext?.libraryName ?? '');
  const storageKey = `${gId}::${selKey}::${defsKey}::m:${meshSlot}::dbg:${debugSlot}::lib:${libSlot}`;
  const cached = evalCache.get(storageKey);
  if (cached) {
    return cached;
  }

  const nodeMap = new Map<string, MaterialGraphNode>();
  for (const n of graph.nodes) nodeMap.set(n.id, n);

  const outputs = new Map<string, Map<string, any>>();
  const createdTextures: THREE.Texture[] = [];

  const getInput = (nodeId: string, handle: string): any => {
    const edge = graph.edges.find((e) => e.target === nodeId && e.targetHandle === handle);
    if (!edge) return undefined;
    return outputs.get(edge.source)?.get(edge.sourceHandle);
  };

  /** Color Constant (hex) → texture processors. Without this, Exposure/HSL after Color is a no-op. */
  const coerceTexture = (val: unknown): THREE.Texture | undefined => {
    if (val instanceof THREE.Texture) return val;
    if (typeof val === 'string' && val.trim()) {
      const rgb = rgbFromColorString(val);
      if (!rgb) return undefined;
      const tex = createSolidColorTexture(rgb.r, rgb.g, rgb.b);
      createdTextures.push(tex);
      return tex;
    }
    return undefined;
  };

  const order = topoSort(graph.nodes, graph.edges);

  for (const nodeId of order) {
    const node = nodeMap.get(nodeId);
    if (!node) continue;
    const out = new Map<string, any>();

    switch (node.type) {
      case 'colorConstant': {
        out.set('color', node.data.color ?? '#ffffff');
        break;
      }
      case 'numberConstant': {
        out.set('value', node.data.value ?? 0);
        break;
      }
      case 'attributeInput': {
        const key = node.data.attributeKey as string;
        const outputField = (node.data.outputField as string) || 'value';
        const attrDef = key && attributeDefs ? attributeDefs.find((a) => a.key === key) : undefined;

        let raw: unknown = key ? selections[key] : undefined;
        if (
          (raw === undefined || raw === null || raw === '') &&
          attrDef?.defaultValue != null &&
          String(attrDef.defaultValue).length > 0
        ) {
          raw = attrDef.defaultValue;
        }

        let resolved: unknown = raw ?? node.data.fallback ?? '';

        if (raw != null && raw !== '' && attrDef?.options?.length) {
          const opt = attrDef.options.find((o) => o.key === String(raw));
          if (opt) {
            if (outputField === 'color') {
              resolved = opt.color || node.data.fallback || '';
            } else if (outputField === 'label') {
              resolved = opt.label || node.data.fallback || '';
            } else {
              resolved = String(raw);
            }
          }
        }

        out.set('value', resolved);
        // Same payload on `color` so edges that look for a color handle still resolve.
        if (outputField === 'color') out.set('color', resolved);
        break;
      }
      case 'uvMap': {
        out.set('uv', {
          __uvData: true,
          channel: Math.max(0, Math.min(9, Math.round((node.data.channel as number) ?? 0))),
          tileX: (node.data.tileX as number) ?? 1,
          tileY: (node.data.tileY as number) ?? 1,
          offsetX: (node.data.offsetX as number) ?? 0,
          offsetY: (node.data.offsetY as number) ?? 0,
          rotation: (node.data.rotation as number) ?? 0,
        });
        break;
      }
      case 'imageTexture': {
        const url = node.data.url as string;
        if (url) {
          const uvInput = getInput(nodeId, 'uv') as { __uvData: true; channel: number; tileX: number; tileY: number; offsetX: number; offsetY: number; rotation: number } | undefined;

          let tileX = (node.data.tileX as number) ?? 1;
          let tileY = (node.data.tileY as number) ?? 1;
          let offsetX = 0;
          let offsetY = 0;
          let rotation = 0;
          let channel = 0;

          if (uvInput?.__uvData) {
            tileX = (uvInput.tileX ?? 1) * tileX;
            tileY = (uvInput.tileY ?? 1) * tileY;
            offsetX = uvInput.offsetX ?? 0;
            offsetY = uvInput.offsetY ?? 0;
            rotation = uvInput.rotation ?? 0;
            channel = uvInput.channel ?? 0;
          }

          const needsClone = tileX !== 1 || tileY !== 1 || offsetX !== 0 || offsetY !== 0 || rotation !== 0 || channel !== 0;
          if (needsClone) {
            const tex = loadTex(url);
            const clone = cloneTextureLight(tex);
            if (tex.image && !clone.image) clone.image = tex.image;
            clone.repeat.set(tileX, tileY);
            clone.offset.set(offsetX, offsetY);
            if (rotation !== 0) {
              clone.rotation = (rotation * Math.PI) / 180;
              clone.center.set(0.5, 0.5);
            }
            if (channel > 0) clone.channel = channel;
            clone.wrapS = THREE.RepeatWrapping;
            clone.wrapT = THREE.RepeatWrapping;
            configureStableTextureSampling(clone);
            commitTextureUV(clone);
            safeMarkNeedsUpdate(clone);
            createdTextures.push(clone);
            out.set('texture', clone);
          } else {
            out.set('texture', loadTex(url));
          }
        }
        break;
      }
      case 'textCanvas': {
        const textIn = getInput(nodeId, 'text');
        const colorIn = getInput(nodeId, 'color');
        const fontIn = getInput(nodeId, 'font');
        let text = textIn != null ? String(textIn) : (node.data.text ?? '');
        const color = colorIn ?? node.data.color ?? '#ffffff';

        const scriptEnabledIn = getInput(nodeId, 'scriptEnabled');
        const FALSY_STRINGS = new Set(['', '0', 'false', 'off', 'no', 'disabled']);
        // Unwired → true (script "on"). Wired attribute drives the boolean.
        const scriptEnabledWired = scriptEnabledIn !== undefined;
        const scriptOn = !scriptEnabledWired
          || (typeof scriptEnabledIn === 'string'
              ? !FALSY_STRINGS.has(scriptEnabledIn.toLowerCase().trim())
              : !!scriptEnabledIn);

        // Font: Attribute → font port (option key). When Engraving Dots (script on/off)
        // is also wired, prefer composite keys like `ch:on` / `en:off`, then fall back to `ch`.
        let fontUrl: string | null =
          typeof node.data.fontUrl === 'string' ? node.data.fontUrl : null;
        let fontFamily = (node.data.fontFamily as string) ?? 'Arial';
        let fontSize = typeof node.data.fontSize === 'number' ? node.data.fontSize : 48;
        let maxChars: number | undefined;
        const fontMap = (node.data.fontMap as Record<string, TextCanvasFontMapEntry> | undefined) ?? {};
        if (fontIn != null && String(fontIn).trim() !== '') {
          const mapped = resolveTextCanvasFontMapEntry(
            fontMap,
            String(fontIn).trim(),
            scriptEnabledWired,
            scriptOn,
          );
          if (mapped) {
            if (mapped.fontFamily) fontFamily = mapped.fontFamily;
            if (mapped.fontUrl !== undefined) fontUrl = mapped.fontUrl ?? null;
            if (typeof mapped.fontSize === 'number' && Number.isFinite(mapped.fontSize) && mapped.fontSize > 0) {
              fontSize = mapped.fontSize;
            }
            if (typeof mapped.maxChars === 'number' && Number.isFinite(mapped.maxChars) && mapped.maxChars > 0) {
              maxChars = Math.floor(mapped.maxChars);
            }
          }
        }

        // Clamp user text before Script so maxChars matches the input field (not expanded dots).
        if (maxChars != null) {
          text = truncateGraphemes(text, maxChars);
        }

        const customScript = (node.data.customScript as string) || '';
        if (customScript) {
          try {
            // Always run so scripts can keep layout stable (e.g. join with '·' vs ' ').
            // `scriptOn` is the wired script-on/off value (true when unwired).
            const fn = new Function('text', 'scriptOn', customScript);
            const result = fn(String(text), scriptOn);
            if (typeof result === 'string') text = result;
          } catch (e) {
            console.warn('[material-graph] custom script error:', e);
          }
        }

        const d = node.data;
        const textBudget = textCanvasDeviceBudget();
        const rawW = typeof d.width === 'number' && d.width > 0 ? d.width : 512;
        const rawH = typeof d.height === 'number' && d.height > 0 ? d.height : 512;
        const rawSs = typeof d.superSample === 'number' ? d.superSample : 2;
        const baseOpts = {
          text,
          fontSize,
          fontFamily,
          fontUrl,
          align: (d.align ?? 'center') as 'left' | 'center' | 'right',
          padding: d.padding ?? 16,
          width: Math.min(rawW, textBudget.maxEdge),
          height: Math.min(rawH, textBudget.maxEdge),
          bold: d.bold ?? false,
          italic: d.italic ?? false,
          blur: d.blur ?? 0,
          superSample: Math.min(Math.max(1, Math.round(rawSs)), textBudget.maxSuperSample),
          textTransform: (d.textTransform ?? 'none') as 'none' | 'uppercase' | 'lowercase' | 'capitalize',
          letterSpacing: d.letterSpacing ?? 0,
          wordSpacing: d.wordSpacing ?? 0,
          lineHeight: d.lineHeight ?? 1.25,
          verticalAlign: (d.verticalAlign ?? 'middle') as 'top' | 'middle' | 'bottom',
          direction: (d.direction ?? 'horizontal') as 'horizontal' | 'vertical',
          textOpacity: d.textOpacity ?? 1,
          strokeEnabled: d.strokeEnabled ?? false,
          strokeColor: (d.strokeColor as string) ?? '#000000',
          strokeWidth: d.strokeWidth ?? 2,
          stroke2Enabled: d.stroke2Enabled ?? false,
          stroke2Color: (d.stroke2Color as string) ?? '#000000',
          stroke2Width: d.stroke2Width ?? 4,
          shadowEnabled: d.shadowEnabled ?? false,
          shadowColor: (d.shadowColor as string) ?? '#000000',
          shadowBlur: d.shadowBlur ?? 4,
          shadowOffsetX: d.shadowOffsetX ?? 2,
          shadowOffsetY: d.shadowOffsetY ?? 2,
          shadowOpacity: d.shadowOpacity ?? 0.7,
        };
        const tex = createTextCanvasTexture({
          ...baseOpts,
          color,
          backgroundColor: d.backgroundColor ?? null,
        });
        createdTextures.push(tex);
        out.set('texture', tex);

        const needsBump = graph.edges.some((e) => e.source === nodeId && e.sourceHandle === 'bump');
        const needsOpacity = graph.edges.some((e) => e.source === nodeId && e.sourceHandle === 'opacity');

        if (needsBump) {
          const userBlur = (d.blur as number) ?? 0;
          const bumpTex = createTextCanvasTexture({
            ...baseOpts,
            color: '#ffffff',
            backgroundColor: '#000000',
            blur: Math.max(userBlur, 1.5),
            shadowEnabled: false,
          });
          bumpTex.colorSpace = THREE.LinearSRGBColorSpace;
          createdTextures.push(bumpTex);
          out.set('bump', bumpTex);
        }

        if (needsOpacity) {
          const opacityTex = createTextCanvasTexture({
            ...baseOpts,
            color: '#ffffff',
            backgroundColor: '#000000',
            blur: 0,
            shadowEnabled: false,
          });
          opacityTex.colorSpace = THREE.LinearSRGBColorSpace;
          createdTextures.push(opacityTex);
          out.set('opacity', opacityTex);
        }

        break;
      }
      case 'blend': {
        const texA = coerceTexture(getInput(nodeId, 'base'));
        const texB = coerceTexture(getInput(nodeId, 'layer'));
        const texMask = getInput(nodeId, 'mask') as THREE.Texture | undefined;
        if (nodeMuted(node)) {
          if (texA) out.set('texture', texA);
          break;
        }
        const mode = (node.data.mode as string) ?? 'alpha';
        const opacity = (node.data.opacity as number) ?? 1;
        if (texA || texB) {
          const result = texMask
            ? blendTexturesMasked(texA ?? null, texB ?? null, texMask, mode, opacity)
            : blendTextures(texA ?? null, texB ?? null, mode, opacity);
          if (result) {
            createdTextures.push(result);
            out.set('texture', result);
          }
        }
        break;
      }
      case 'textureToNormal': {
        const texIn = coerceTexture(getInput(nodeId, 'texture'));
        if (nodeMuted(node)) {
          if (texIn) out.set('texture', texIn);
          break;
        }
        if (texIn) {
          const strength = (node.data.strength as number) ?? 1;
          const invertY = !!(node.data.invertY as boolean);
          const result = processTextureToNormalMap(texIn, strength, invertY);
          createdTextures.push(result);
          out.set('texture', result);
        }
        break;
      }
      case 'textureToBump': {
        const texIn = coerceTexture(getInput(nodeId, 'texture'));
        if (nodeMuted(node)) {
          if (texIn) out.set('texture', texIn);
          break;
        }
        if (texIn) {
          const result = processTexture(texIn, (r, g, b, a) => {
            const v = 0.299 * r + 0.587 * g + 0.114 * b;
            return [v, v, v, a];
          }, 'textureToBump', PRESERVE_SOURCE_UV);
          result.colorSpace = THREE.LinearSRGBColorSpace;
          result.needsUpdate = true;
          createdTextures.push(result);
          out.set('texture', result);
        }
        break;
      }
      case 'uvTransform': {
        const portCount = Math.max(1, Math.min(8, (node.data.portCount as number) || 1));
        const repeatX = (node.data.repeatX as number) ?? 1;
        const repeatY = (node.data.repeatY as number) ?? 1;
        const offsetX = (node.data.offsetX as number) ?? 0;
        const offsetY = (node.data.offsetY as number) ?? 0;
        const rotation = (node.data.rotation as number) ?? 0;
        const tm = (node.data.tilingMode as string) || (node.data.clamp ? 'clamp' : 'repeat');
        const wrap = tm === 'mirrored' ? THREE.MirroredRepeatWrapping
          : tm === 'clamp' ? THREE.ClampToEdgeWrapping
          : THREE.RepeatWrapping;

        for (let p = 0; p < portCount; p++) {
          const portId = p === 0 ? 'texture' : `tex${p + 1}`;
          const texIn = coerceTexture(getInput(nodeId, portId));
          if (nodeMuted(node)) {
            if (texIn) out.set(portId, texIn);
            continue;
          }
          if (texIn) {
            const clone = cloneTextureLight(texIn);
            if (texIn.image && !clone.image) clone.image = texIn.image;
            clone.colorSpace = texIn.colorSpace;
            // Pending CPU bake upstream (HSL / Exposure / …): point at the source via
            // `__c3dBakeSource` — do NOT share userData. Sharing made promotePending run
            // trySyncFinish on this UV clone and adoptTextureUv(identity), wiping Tile/Off/Rot
            // (Gradient / Metal Mesh / Straw looked correct in editor, wrong in player).
            const bakePending = !!(texIn.userData as any)?.__c3dBakePending;
            if (bakePending) {
              clone.userData = {
                ...(clone.userData ?? {}),
                __c3dBakePending: true,
                __c3dBakeSource: texIn,
                __c3dBakeStandIn: (texIn.userData as any).__c3dBakeStandIn,
              };
              delete (clone.userData as any).__c3dCpuProcessCached;
              delete (clone.userData as any).__c3dProcessFn;
              delete (clone.userData as any).__c3dProcessSource;
              delete (clone.userData as any).__c3dProcessInput;
              delete (clone.userData as any).__c3dProcessUvAdoptFrom;
              delete (clone.userData as any).__c3dSyncFinish;
            }
            safeMarkNeedsUpdate(clone);
            clone.repeat.set(repeatX, repeatY);
            clone.offset.set(offsetX, offsetY);
            clone.rotation = (rotation * Math.PI) / 180;
            clone.center.set(0.5, 0.5);
            clone.wrapS = wrap;
            clone.wrapT = wrap;
            configureStableTextureSampling(clone);
            commitTextureUV(clone);
            createdTextures.push(clone);
            out.set(portId, clone);
          }
        }
        break;
      }
      case 'uvRandomize': {
        const portCount = Math.max(1, Math.min(8, (node.data.portCount as number) || 1));
        const muted = nodeMuted(node);
        const d = node.data as Record<string, unknown>;
        const perMesh = !!d.perMesh;
        const baseSeed = Number(d.seed) || 0;
        const meshKey = String(evalContext?.meshName ?? '');
        const seed = perMesh ? combineUvRandomizeSeed(baseSeed, meshKey) : baseSeed;

        const scaleJitter = Math.min(1, Math.max(0, Number(d.scaleJitter) || 0));

        const minU = typeof d.offsetMinU === 'number' ? d.offsetMinU : 0;
        const maxU = typeof d.offsetMaxU === 'number' ? d.offsetMaxU : 0;
        const minV = typeof d.offsetMinV === 'number' ? d.offsetMinV : 0;
        const maxV = typeof d.offsetMaxV === 'number' ? d.offsetMaxV : 0;
        const loU = Math.min(minU, maxU);
        const hiU = Math.max(minU, maxU);
        const loV = Math.min(minV, maxV);
        const hiV = Math.max(minV, maxV);

        const rotMin = typeof d.rotationMinDeg === 'number' ? d.rotationMinDeg : 0;
        const rotMax = typeof d.rotationMaxDeg === 'number' ? d.rotationMaxDeg : 0;
        const loR = Math.min(rotMin, rotMax);
        const hiR = Math.max(rotMin, rotMax);

        const repeatX = (d.repeatX as number) ?? 1;
        const repeatY = (d.repeatY as number) ?? 1;
        const rmiX = d.repeatMinX;
        const rmaX = d.repeatMaxX;
        const rmiY = d.repeatMinY;
        const rmaY = d.repeatMaxY;

        const offsetStepU =
          typeof d.offsetStepU === 'number' && d.offsetStepU > 0 ? d.offsetStepU : 0;
        const offsetStepV =
          typeof d.offsetStepV === 'number' && d.offsetStepV > 0 ? d.offsetStepV : 0;
        const rotationStepDeg =
          typeof d.rotationStepDeg === 'number' && d.rotationStepDeg > 0 ? d.rotationStepDeg : 0;
        const repeatStepX =
          typeof d.repeatStepX === 'number' && d.repeatStepX > 0 ? d.repeatStepX : 0;
        const repeatStepY =
          typeof d.repeatStepY === 'number' && d.repeatStepY > 0 ? d.repeatStepY : 0;

        const mirrorU = !!d.mirrorU;
        const mirrorV = !!d.mirrorV;

        // One roll for all ports so albedo/normal/etc stay UV-aligned.
        let roll: { ru: number; rv: number; rotDeg: number; rx: number; ry: number } | null = null;
        let uvMeta: C3dUvRandomizeMeta | null = null;

        const ensureRoll = () => {
          if (roll) return;
          const rand = createSeededRng(seed);
          const ru = randomInRangeWithStep(loU, hiU, offsetStepU, rand);
          const rv = randomInRangeWithStep(loV, hiV, offsetStepV, rand);
          const rotDeg = randomInRangeWithStep(loR, hiR, rotationStepDeg, rand);

          let rx: number;
          if (typeof rmiX === 'number' && typeof rmaX === 'number' && rmaX > rmiX) {
            rx = randomInRangeWithStep(rmiX, rmaX, repeatStepX, rand);
          } else {
            rx = repeatX;
            if (scaleJitter > 0) rx *= 1 + (rand() * 2 - 1) * scaleJitter;
          }

          let ry: number;
          if (typeof rmiY === 'number' && typeof rmaY === 'number' && rmaY > rmiY) {
            ry = randomInRangeWithStep(rmiY, rmaY, repeatStepY, rand);
          } else {
            ry = repeatY;
            if (scaleJitter > 0) ry *= 1 + (rand() * 2 - 1) * scaleJitter;
          }

          // Negative repeat + center 0.5 mirrors the texture around the UV midpoint.
          if (mirrorU && rand() < 0.5) rx = -Math.abs(rx) || -1;
          if (mirrorV && rand() < 0.5) ry = -Math.abs(ry) || -1;

          // Per mesh / array copy: keep mapTransform stable — variation is driven by
          // instance UV (and mat.userData meta). Baking a late random into the texture
          // when images finish decoding caused a ~0.3s UV flash on straw switches.
          if (perMesh) {
            roll = { ru: 0, rv: 0, rotDeg: 0, rx: 1, ry: 1 };
          } else {
            roll = { ru, rv, rotDeg, rx, ry };
          }

          let scaleMin = 1;
          let scaleMax = 1;
          let scaleStep = 0;
          if (typeof rmiX === 'number' && typeof rmaX === 'number') {
            scaleMin = Math.min(rmiX, rmaX);
            scaleMax = Math.max(rmiX, rmaX);
            scaleStep = repeatStepX;
          } else if (typeof rmiY === 'number' && typeof rmaY === 'number') {
            scaleMin = Math.min(rmiY, rmaY);
            scaleMax = Math.max(rmiY, rmaY);
            scaleStep = repeatStepY;
          } else if (scaleJitter > 0) {
            scaleMin = Math.max(0.01, 1 - scaleJitter);
            scaleMax = 1 + scaleJitter;
          }
          uvMeta = {
            perMesh,
            seed: baseSeed,
            offsetMinU: loU,
            offsetMaxU: hiU,
            offsetStepU: offsetStepU,
            offsetMinV: loV,
            offsetMaxV: hiV,
            offsetStepV: offsetStepV,
            rotationMinDeg: loR,
            rotationMaxDeg: hiR,
            rotationStepDeg: rotationStepDeg,
            scaleMin,
            scaleMax,
            scaleStep,
            mirrorU,
            mirrorV,
          };
        };

        for (let p = 0; p < portCount; p++) {
          const portId = p === 0 ? 'texture' : `tex${p + 1}`;
          const texIn = coerceTexture(getInput(nodeId, portId));
          if (!texIn) continue;
          if (muted) {
            out.set(portId, texIn);
            continue;
          }
          ensureRoll();
          const { ru, rv, rotDeg, rx, ry } = roll!;

          const clone = cloneTextureLight(texIn);
          if (texIn.image && !clone.image) clone.image = texIn.image;
          clone.colorSpace = texIn.colorSpace;
          safeMarkNeedsUpdate(clone);
          // Compose with upstream UV (e.g. UV Transform) — do not replace it.
          const baseRepeatX = texIn.repeat?.x ?? 1;
          const baseRepeatY = texIn.repeat?.y ?? 1;
          const baseOffsetX = texIn.offset?.x ?? 0;
          const baseOffsetY = texIn.offset?.y ?? 0;
          const baseRot = texIn.rotation ?? 0;
          clone.repeat.set(baseRepeatX * rx, baseRepeatY * ry);
          clone.offset.set(baseOffsetX + ru, baseOffsetY + rv);
          clone.rotation = baseRot + (rotDeg * Math.PI) / 180;
          if (texIn.center) clone.center.copy(texIn.center);
          else clone.center.set(0.5, 0.5);
          clone.wrapS = texIn.wrapS;
          clone.wrapT = texIn.wrapT;
          configureStableTextureSampling(clone);
          commitTextureUV(clone);
          const bakePending = !!(texIn.userData as any)?.__c3dBakePending;
          clone.userData = {
            ...(clone.userData ?? {}),
            [C3D_UV_RANDOMIZE_UD]: uvMeta,
            ...(bakePending
              ? {
                  __c3dBakePending: true,
                  // Wait for the pending CPU canvas (texIn), not the raw diffuse.
                  __c3dBakeSource: texIn,
                  __c3dBakeStandIn: (texIn.userData as any).__c3dBakeStandIn,
                }
              : {}),
          };
          if (bakePending) {
            delete (clone.userData as any).__c3dCpuProcessCached;
            delete (clone.userData as any).__c3dProcessFn;
            delete (clone.userData as any).__c3dProcessSource;
            delete (clone.userData as any).__c3dProcessInput;
            delete (clone.userData as any).__c3dProcessUvAdoptFrom;
            delete (clone.userData as any).__c3dSyncFinish;
          }
          createdTextures.push(clone);
          out.set(portId, clone);
        }
        break;
      }
      case 'invert': {
        const texIn = coerceTexture(getInput(nodeId, 'texture'));
        if (nodeMuted(node)) {
          if (texIn) out.set('texture', texIn);
          break;
        }
        if (texIn) {
          const strength = (node.data.strength as number) ?? 1;
          const result = processTexture(texIn, (r, g, b, a) => {
            const s = Math.min(Math.max(strength, 0), 1);
            return [
              r + s * (255 - 2 * r),
              g + s * (255 - 2 * g),
              b + s * (255 - 2 * b),
              a,
            ];
          }, `invert:${strength}`, PRESERVE_SOURCE_UV);
          if (result) { createdTextures.push(result); out.set('texture', result); }
        }
        break;
      }
      case 'brightnessContrast': {
        const texIn = coerceTexture(getInput(nodeId, 'texture'));
        if (nodeMuted(node)) {
          if (texIn) out.set('texture', texIn);
          break;
        }
        if (texIn) {
          const brightnessRaw = node.data.brightness;
          const contrastRaw = node.data.contrast;
          const brightness = Number.isFinite(Number(brightnessRaw)) ? Number(brightnessRaw) : 0;
          const contrast = Number.isFinite(Number(contrastRaw)) ? Number(contrastRaw) : 0;
          // Neutral tone adjustment should be a true pass-through. Avoid CPU
          // baking so the texture doesn't temporarily disappear while the
          // placeholder canvas is pending.
          const eps = 1e-4;
          if (Math.abs(brightness) < eps && Math.abs(contrast) < eps) {
            out.set('texture', texIn);
            break;
          }
          const factor = (259 * (contrast * 255 + 255)) / (255 * (259 - contrast * 255));
          const bOffset = brightness * 255;
          const result = processTexture(texIn, (r, g, b, a) => [
            clamp255(factor * (r - 128) + 128 + bOffset),
            clamp255(factor * (g - 128) + 128 + bOffset),
            clamp255(factor * (b - 128) + 128 + bOffset),
            a,
          ], `bc:b${brightness}:c${contrast}`, PRESERVE_SOURCE_UV);
          if (result) { createdTextures.push(result); out.set('texture', result); }
        }
        break;
      }
      case 'exposureGamma': {
        const texIn = coerceTexture(getInput(nodeId, 'texture'));
        if (nodeMuted(node)) {
          if (texIn) out.set('texture', texIn);
          break;
        }
        if (texIn) {
          const exposure = typeof node.data.exposure === 'number' ? node.data.exposure : 0;
          const offset = typeof node.data.offset === 'number' ? node.data.offset : 0;
          let gamma = typeof node.data.gamma === 'number' ? node.data.gamma : 1;
          if (!Number.isFinite(gamma) || gamma < 0.01) gamma = 0.01;
          if (gamma > 10) gamma = 10;
          const expMul = Math.pow(2, exposure);
          const invGamma = 1 / gamma;
          const eps = 1e-4;
          if (Math.abs(exposure) < eps && Math.abs(offset) < eps && Math.abs(gamma - 1) < eps) {
            out.set('texture', texIn);
            break;
          }
          const result = processTexture(texIn, (r, g, b, a) => {
            const apply = (v: number) => {
              let c = (v / 255) * expMul + offset;
              c = Math.min(1, Math.max(0, c));
              c = Math.pow(Math.max(c, 1e-8), invGamma);
              return clamp255(c * 255);
            };
            return [apply(r), apply(g), apply(b), a];
          }, `eg:e${exposure}:o${offset}:g${gamma}`, PRESERVE_SOURCE_UV);
          if (result) { createdTextures.push(result); out.set('texture', result); }
        }
        break;
      }
      case 'blurSharpen': {
        const texIn = coerceTexture(getInput(nodeId, 'texture'));
        if (nodeMuted(node)) {
          if (texIn) out.set('texture', texIn);
          break;
        }
        if (texIn) {
          const strength = typeof node.data.strength === 'number' ? node.data.strength : 0;
          const radius = typeof node.data.radius === 'number' ? node.data.radius : 2;
          const opacitySource = parseBlurSharpenOpacityChannel(node.data.opacityChannel);
          const result = processBlurSharpenTexture(texIn, strength, radius, opacitySource);
          createdTextures.push(result);
          out.set('texture', result);
        }
        break;
      }
      case 'desaturate': {
        const texIn = coerceTexture(getInput(nodeId, 'texture'));
        if (nodeMuted(node)) {
          if (texIn) out.set('texture', texIn);
          break;
        }
        if (texIn) {
          const amount = Math.min(Math.max((node.data.amount as number) ?? 1, 0), 1);
          const result = processTexture(texIn, (r, g, b, a) => {
            const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
            return [
              r + amount * (lum - r),
              g + amount * (lum - g),
              b + amount * (lum - b),
              a,
            ];
          }, `desat:${amount}`, PRESERVE_SOURCE_UV);
          if (result) { createdTextures.push(result); out.set('texture', result); }
        }
        break;
      }
      case 'hslAdjust': {
        const texIn = coerceTexture(getInput(nodeId, 'texture'));
        if (nodeMuted(node)) {
          if (texIn) out.set('texture', texIn);
          break;
        }
        if (texIn) {
          const hueShift = (node.data.hue as number) ?? 0;
          const satShift = (node.data.saturation as number) ?? 0;
          const lumShift = (node.data.lightness as number) ?? 0;
          const result = processTexture(texIn, (r, g, b, a) => {
            const [h, s, l] = rgbToHsl(r / 255, g / 255, b / 255);
            const nh = ((h + hueShift / 360) % 1 + 1) % 1;
            const ns = Math.min(Math.max(s + satShift, 0), 1);
            const nl = Math.min(Math.max(l + lumShift, 0), 1);
            const [nr, ng, nb] = hslToRgb(nh, ns, nl);
            return [nr * 255, ng * 255, nb * 255, a];
          }, `hsl:h${hueShift}:s${satShift}:l${lumShift}`, PRESERVE_SOURCE_UV);
          if (result) { createdTextures.push(result); out.set('texture', result); }
        }
        break;
      }
      case 'clamp': {
        const texIn = coerceTexture(getInput(nodeId, 'texture'));
        if (nodeMuted(node)) {
          if (texIn) out.set('texture', texIn);
          break;
        }
        if (texIn) {
          const lo = (node.data.min as number) ?? 0;
          const hi = (node.data.max as number) ?? 1;
          const range = hi - lo || 1;
          const result = processTexture(texIn, (r, g, b, a) => [
            clamp255(((r / 255 - lo) / range) * 255),
            clamp255(((g / 255 - lo) / range) * 255),
            clamp255(((b / 255 - lo) / range) * 255),
            a,
          ], `clamp:${lo}:${hi}`, PRESERVE_SOURCE_UV);
          if (result) { createdTextures.push(result); out.set('texture', result); }
        }
        break;
      }
      case 'colorAdjust': {
        const colorIn = getInput(nodeId, 'color');
        const fallback = (node.data.fallback as string) ?? '#ffffff';
        const attrKey = String((node.data.attributeKey as string) || '').trim();
        const raw = colorIn != null && String(colorIn).trim() !== '' ? String(colorIn).trim() : '';
        let effective: string | null = null;
        if (raw) {
          if (rgbFromColorString(raw)) effective = raw;
          else if (attrKey) {
            const sw = resolveAttributeOptionColor(attrKey, selections, attributeDefs, raw);
            if (sw && rgbFromColorString(sw)) effective = sw;
          }
        } else if (attrKey) {
          const sw = resolveAttributeOptionColor(attrKey, selections, attributeDefs);
          if (sw && rgbFromColorString(sw)) effective = sw;
        }
        if (!effective && rgbFromColorString(fallback)) effective = fallback;
        if (!effective) {
          out.set('color', '#ffffff');
          break;
        }
        if (nodeMuted(node)) {
          const c = rgbFromColorString(effective)!;
          out.set('color', formatHexRgb(c.r, c.g, c.b));
          break;
        }
        const multiply = (node.data.multiply as number) ?? 1;
        const lightness = (node.data.lightness as number) ?? 0;
        out.set('color', adjustWiredHexColor(effective, multiply, lightness));
        break;
      }
      case 'colorToTexture': {
        const colorIn = getInput(nodeId, 'color');
        const fallback = (node.data.fallback as string) ?? '#ffffff';
        const attrKey = String((node.data.attributeKey as string) || '').trim();
        const raw = colorIn != null && String(colorIn).trim() !== '' ? String(colorIn).trim() : '';
        let rgb: { r: number; g: number; b: number } | null = null;
        if (raw) {
          rgb = rgbFromColorString(raw);
          if (!rgb && attrKey) {
            const sw = resolveAttributeOptionColor(attrKey, selections, attributeDefs, raw);
            if (sw) rgb = rgbFromColorString(sw);
          }
        } else if (attrKey) {
          const sw = resolveAttributeOptionColor(attrKey, selections, attributeDefs);
          if (sw) rgb = rgbFromColorString(sw);
        }
        if (!rgb) rgb = rgbFromColorString(fallback);
        if (!rgb) break;
        const tex = createSolidColorTexture(rgb.r, rgb.g, rgb.b);
        createdTextures.push(tex);
        out.set('texture', tex);
        break;
      }
      case 'falloff': {
        const frontIn = getInput(nodeId, 'frontColor');
        const sideIn = getInput(nodeId, 'sideColor');
        const powerIn = getInput(nodeId, 'power');
        const front = (frontIn as string) ?? (node.data.frontColor as string) ?? '#ffffff';
        if (nodeMuted(node)) {
          out.set('color', front);
          break;
        }
        const side = (sideIn as string) ?? (node.data.sideColor as string) ?? '#000000';
        const power = (powerIn as number) ?? (node.data.power as number) ?? 2;
        out.set('color', { __falloff: true, front, side, power });
        break;
      }
      case 'roundedEdge': {
        if (nodeMuted(node)) break;
        const radiusIn = getInput(nodeId, 'radius');
        const samplesIn = getInput(nodeId, 'samples');
        const radius = (radiusIn as number) ?? (node.data.radius as number) ?? 5;
        const samples = (samplesIn as number) ?? (node.data.samples as number) ?? 1;
        out.set('normal', { __roundedEdge: true, radius, samples: Math.max(1, Math.round(samples)) });
        break;
      }
      case 'pbrOutput':
      case 'materialBlender': {
        break;
      }
    }
    outputs.set(nodeId, out);
  }

  // ── Helper: resolve a single PBR Output node into overrides ──
  const PBR_SLOT_MAP: Record<string, string> = {
    color: 'color', map: 'map', normalMap: 'normalMap', normalScale: 'normalScale',
    bumpMap: 'bumpMap', bumpScale: 'bumpScale', roughnessMap: 'roughnessMap',
    metalnessMap: 'metalnessMap', emissiveMap: 'emissiveMap', aoMap: 'aoMap',
    alphaMap: 'alphaMap', displacementMap: 'displacementMap', roughness: 'roughness',
    metalness: 'metalness', opacity: 'opacity', emissive: 'emissive',
    emissiveIntensity: 'emissiveIntensity', transmission: 'transmission', ior: 'ior',
    thickness: 'thickness', transmitBlur: 'transmitBlur',
    attenuationColor: 'attenuationColor', attenuationDistance: 'attenuationDistance',
    clearcoat: 'clearcoat',
    clearcoatMap: 'clearcoatMap', clearcoatRoughness: 'clearcoatRoughness',
    clearcoatRoughnessMap: 'clearcoatRoughnessMap', clearcoatNormalMap: 'clearcoatNormalMap',
    sheenColor: 'sheenColor', sheenRoughness: 'sheenRoughness',
    specularIntensity: 'specularIntensity', specularColor: 'specularColor',
    envMapIntensity: 'envMapIntensity', anisotropy: 'anisotropy',
    anisotropyRotation: 'anisotropyRotation', anisotropyMap: 'anisotropyMap',
  };
  const NUMERIC_TO_MAP: Record<string, string> = {
    opacity: 'alphaMap', roughness: 'roughnessMap', metalness: 'metalnessMap',
  };

  /**
   * Unchecked PBR Output ports mean "do not drive this from the graph". For GLB meshes the player
   * only applies keys present on `overrides`; omitted keys would keep the original glTF values.
   * Emit explicit neutral values so disabled slots actually turn features off in the viewer.
   */
  function applyDisabledPbrSlot(
    handle: string,
    _prop: string,
    result: Record<string, any>,
    nodeData?: Record<string, any>,
  ): void {
    switch (handle) {
      case 'clearcoat':
        result.clearcoat = 0;
        break;
      case 'clearcoatRoughness':
        result.clearcoatRoughness = 0;
        break;
      case 'clearcoatMap':
        result.clearcoatMap = null;
        break;
      case 'clearcoatRoughnessMap':
        result.clearcoatRoughnessMap = null;
        break;
      case 'clearcoatNormalMap':
        result.clearcoatNormalMap = null;
        break;
      case 'transmission':
        result.transmission = 0;
        break;
      case 'transmitBlur':
        result.transmitBlur = 0;
        break;
      case 'ior':
        result.ior = 1.5;
        break;
      case 'thickness':
        result.thickness = 0;
        break;
      case 'attenuationColor':
        // Disabled → no tint drive (auto / clear).
        delete result.attenuationColor;
        break;
      case 'attenuationDistance':
        // Disabled → auto / Infinity; never emit 0.
        delete result.attenuationDistance;
        break;
      case 'opacity':
        result.opacity = 1;
        break;
      case 'alphaMap':
        result.alphaMap = null;
        break;
      case 'emissive':
        result.emissive = '#000000';
        break;
      case 'emissiveIntensity':
        result.emissiveIntensity = 0;
        break;
      case 'emissiveMap':
        result.emissiveMap = null;
        break;
      case 'sheenColor':
        result.sheenColor = '#000000';
        break;
      case 'sheenRoughness':
        result.sheenRoughness = 0;
        break;
      case 'specularIntensity':
        result.specularIntensity = 1;
        break;
      case 'specularColor':
        result.specularColor = '#ffffff';
        break;
      case 'envMapIntensity':
        result.envMapIntensity = 1;
        break;
      case 'anisotropy':
        result.anisotropy = 0;
        break;
      case 'anisotropyRotation':
        result.anisotropyRotation = 0;
        break;
      case 'anisotropyMap':
        result.anisotropyMap = null;
        break;
      case 'normalMap':
        result.normalMap = null;
        break;
      case 'normalScale':
        result.normalScale = 1;
        break;
      case 'bumpMap':
        result.bumpMap = null;
        break;
      case 'bumpScale':
        result.bumpScale = 1;
        break;
      case 'aoMap':
        result.aoMap = null;
        break;
      case 'displacementMap':
        result.displacementMap = null;
        break;
      case 'color':
        result.color = '#ffffff';
        break;
      case 'map':
        result.map = null;
        break;
      case 'metalness':
        result.metalness =
          typeof nodeData?.default_metalness === 'number' ? nodeData.default_metalness : 0;
        // Texture wires to the Metalness port become metalnessMap — clear both when unchecked.
        result.metalnessMap = null;
        break;
      case 'roughness':
        // Prefer the authored default so unchecking the slot doesn't lock you out of
        // tuning the constant (disable = "don't drive from wire/graph slot semantics",
        // but the stored number still defines what you want when/if applied).
        result.roughness =
          typeof nodeData?.default_roughness === 'number' ? nodeData.default_roughness : 1;
        result.roughnessMap = null;
        break;
      case 'roughnessMap':
        result.roughnessMap = null;
        break;
      case 'metalnessMap':
        result.metalnessMap = null;
        break;
      default:
        break;
    }
  }

  function resolvePBR(pbrNode: MaterialGraphNode): Record<string, any> {
    const result: Record<string, any> = {};
    const nodeData = (pbrNode.data as Record<string, any>) ?? {};
    /** Same graph texture wired to multiple PBR slots with different UV sets needs separate Texture instances (`.channel` is per-object). */
    const firstUvChannelBySource = new Map<THREE.Texture, number>();
    const colorSpaceByTex = new Map<THREE.Texture, string>();
    const colorSpaceForPbrHandle = (handle: string): string | null => {
      const redirect = NUMERIC_TO_MAP[handle];
      const slot = redirect ?? handle;
      if (LINEAR_SLOTS.has(handle) || LINEAR_SLOTS.has(slot)) return THREE.LinearSRGBColorSpace;
      if (handle === 'color' || slot === 'map' || slot === 'emissiveMap' || slot === 'sheenColorMap') {
        return THREE.SRGBColorSpace;
      }
      return null;
    };
    const textureForSlotUv = (source: THREE.Texture, handle: string): THREE.Texture => {
      const raw = nodeData[`uvChannel_${handle}`];
      const ch = typeof raw === 'number' ? Math.max(0, Math.min(9, Math.round(raw))) : 0;
      const prev = firstUvChannelBySource.get(source);
      let tex = source;
      if (prev === undefined) {
        firstUvChannelBySource.set(source, ch);
        if (ch > 0) tex.channel = ch;
      } else if (prev !== ch) {
        tex = cloneTextureLight(source);
        tex.channel = ch > 0 ? ch : 0;
        tex.colorSpace = source.colorSpace;
        safeMarkNeedsUpdate(tex);
        createdTextures.push(tex);
      }
      const want = colorSpaceForPbrHandle(handle);
      if (want) {
        const tagged = colorSpaceByTex.get(tex);
        if (tagged !== undefined && tagged !== want) {
          tex = cloneTextureLight(tex);
          tex.channel = (tex.channel as number | undefined) ?? (ch > 0 ? ch : 0);
          safeMarkNeedsUpdate(tex);
          createdTextures.push(tex);
        }
        tex.colorSpace = want;
        colorSpaceByTex.set(tex, want);
        safeMarkNeedsUpdate(tex);
      }
      return tex;
    };

    for (const [handle, prop] of Object.entries(PBR_SLOT_MAP)) {
      if (nodeData[`disabled_${handle}`]) {
        applyDisabledPbrSlot(handle, prop, result, nodeData);
        continue;
      }
      const val = getInput(pbrNode.id, handle);
      if (val !== undefined) {
        if (val && typeof val === 'object' && val.__falloff) {
          result._falloff = { front: val.front, side: val.side, power: val.power, slot: prop };
          continue;
        }
        if (val && typeof val === 'object' && val.__roundedEdge) {
          result._roundedEdge = { radius: val.radius, samples: val.samples };
          continue;
        }
        if (val instanceof THREE.Texture) {
          // Editor "Color" port accepts tints or textures; Three.js albedo lives on `map`
          // with `color` as multiply tint. A texture wired here is the albedo itself —
          // use white tint (not the stale Color picker) so greyscale/bump previews aren't
          // still multiplied by the previous solid color.
          if (handle === 'color') {
            // colorToTexture → Color socket: keep a string tint so Metal Yellow↔Brown lerps
            // like Leather. Putting the solid canvas on `map` forced a texSig change → snap.
            const solid = (val.userData as Record<string, unknown> | undefined)?.__c3dSolidColor;
            if (typeof solid === 'string' && solid) {
              result.color = solid;
              continue;
            }
            const tex = textureForSlotUv(val, 'color');
            tex.colorSpace = THREE.SRGBColorSpace;
            configureStableTextureSampling(tex);
            safeMarkNeedsUpdate(tex);
            result.map = tex;
            result.color = '#ffffff';
            continue;
          }
          // Three.js anisotropyMap (lights_physical_fragment):
          //   direction = normalize(2*RG - 1);  strength = length(direction) * B * scalarAnisotropy
          // Mid-gray RG → direction ≈ (0,0) → strength 0. Never pack grayscale as R=G=gray.
          //   Anisotropy (number port) + texture → grayscale strength along +U
          //   Aniso Rot / Aniso Map + chroma (flow) → keep RG, force B=1
          //   Aniso Rot / Aniso Map + grayscale → luminance = rotation angle (0..1 → 0..2π), B=1
          // Pack must NOT bake UV Transform into pixels — Tile/Off/Rot stay on the GPU matrix
          // (same as Color/Normal). Baking UV + caching by image URL made UV Transform appear dead.
          if (
            handle === 'anisotropy' ||
            handle === 'anisotropyRotation' ||
            handle === 'anisotropyMap'
          ) {
            const asStrengthOnly = handle === 'anisotropy';
            const baked = processTexture(
              val,
              (r, g, b, a) => {
                const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                const maxc = Math.max(r, g, b);
                const minc = Math.min(r, g, b);
                const isGray = maxc - minc < 20;
                if (asStrengthOnly) {
                  return [255, 128, clamp255(lum * 255), a];
                }
                // Proper RGB flow / Filament maps: preserve direction.
                if (!isGray) {
                  return [r, g, 255, a];
                }
                // Grayscale on Aniso Rot or Aniso Map → angle map.
                const ang = lum * Math.PI * 2;
                return [
                  clamp255((Math.cos(ang) * 0.5 + 0.5) * 255),
                  clamp255((Math.sin(ang) * 0.5 + 0.5) * 255),
                  255,
                  a,
                ];
              },
              asStrengthOnly ? 'anisoStrPack3' : 'anisoDirPack3',
              { preserveSourceUv: true },
            );
            createdTextures.push(baked);
            // Multiple aniso texture ports share one Three.js map — combine instead of last-wins.
            const prevMap = result.anisotropyMap;
            if (prevMap instanceof THREE.Texture && prevMap !== baked) {
              // RG from direction (Rot/Map), B from strength (Anisotropy port).
              const dirMap = asStrengthOnly ? prevMap : baked;
              const strMap = asStrengthOnly ? baked : prevMap;
              const combined = combineAnisotropyMaps(dirMap, strMap);
              createdTextures.push(combined);
              adoptTextureUv(combined, asStrengthOnly ? prevMap : val);
              result.anisotropyMap = textureForSlotUv(combined, 'anisotropyMap');
            } else {
              const tex = textureForSlotUv(baked, 'anisotropyMap');
              tex.colorSpace = THREE.LinearSRGBColorSpace;
              safeMarkNeedsUpdate(tex);
              result.anisotropyMap = tex;
            }
            result.anisotropyMap.colorSpace = THREE.LinearSRGBColorSpace;
            safeMarkNeedsUpdate(result.anisotropyMap);
            // Texture on the strength port must not wipe the numeric slider (and must stay a number).
            if (asStrengthOnly) {
              const d = nodeData.default_anisotropy;
              result.anisotropy = typeof d === 'number' && d > 0 ? d : 1;
            }
            continue;
          }
          const tex = textureForSlotUv(val, handle);
          const redirect = NUMERIC_TO_MAP[handle];
          if (redirect) {
            if (LINEAR_SLOTS.has(redirect)) { tex.colorSpace = THREE.LinearSRGBColorSpace; safeMarkNeedsUpdate(tex); }
            if (redirect === 'normalMap' || redirect === 'bumpMap' || redirect === 'clearcoatNormalMap') {
              configureNormalMapSampling(tex);
            } else {
              configureStableTextureSampling(tex);
            }
            result[redirect] = tex;
            // Texture on Roughness/Metalness is a *modulator* — still apply the PBR slider
            // (default_*). Skipping this left roughness at the hardcoded 0.5 fallback while a
            // dark roughnessMap kept Wood shiny even when the UI said 1.
            if (handle === 'roughness') {
              result.roughness =
                typeof nodeData.default_roughness === 'number' ? nodeData.default_roughness : 1;
            } else if (handle === 'metalness') {
              result.metalness =
                typeof nodeData.default_metalness === 'number' ? nodeData.default_metalness : 0;
            } else if (handle === 'opacity') {
              const d = nodeData.default_opacity;
              result.opacity = typeof d === 'number' ? d : 1;
            }
            continue;
          }
          if (LINEAR_SLOTS.has(handle)) { tex.colorSpace = THREE.LinearSRGBColorSpace; safeMarkNeedsUpdate(tex); }
          if (handle === 'normalMap' || handle === 'bumpMap' || handle === 'clearcoatNormalMap') {
            configureNormalMapSampling(tex);
          } else {
            configureStableTextureSampling(tex);
          }
          result[prop] = tex;
          continue;
        }
        result[prop] = val;
      } else {
        const def = nodeData[`default_${handle}`];
        if (def !== undefined && def !== null && def !== '') {
          result[prop] = typeof def === 'string' && !isNaN(Number(def)) ? Number(def) : def;
        }
      }
    }
    if (result.map instanceof THREE.Texture) {
      result.map.colorSpace = THREE.SRGBColorSpace;
      safeMarkNeedsUpdate(result.map);
    }
    // With a map, scalar anisotropy must be > 0 or Three.js zeros the sample (see anisotropyVector in WebGLMaterials).
    if (
      !nodeData.disabled_anisotropy &&
      result.anisotropyMap != null &&
      (result.anisotropy == null || result.anisotropy === 0)
    ) {
      result.anisotropy = 1;
    }
    // Aniso Rot / Aniso Map often bypass UV Transform while Color/Normal are tiled+rotated.
    // Thumbnail spheres hide that mismatch; on the product mesh the direction map samples at
    // 1× and the weave reads as isotropic / "wrong". Inherit UV from the primary textured slot.
    if (result.anisotropyMap instanceof THREE.Texture) {
      const sibling =
        (result.map instanceof THREE.Texture && result.map) ||
        (result.normalMap instanceof THREE.Texture && result.normalMap) ||
        (result.roughnessMap instanceof THREE.Texture && result.roughnessMap) ||
        null;
      if (sibling && textureUvIsIdentity(result.anisotropyMap) && !textureUvIsIdentity(sibling)) {
        const aligned = cloneTextureLight(result.anisotropyMap);
        if (result.anisotropyMap.image && !aligned.image) aligned.image = result.anisotropyMap.image;
        aligned.colorSpace = result.anisotropyMap.colorSpace;
        aligned.repeat.copy(sibling.repeat);
        aligned.offset.copy(sibling.offset);
        aligned.rotation = sibling.rotation;
        aligned.center.copy(sibling.center);
        aligned.wrapS = sibling.wrapS;
        aligned.wrapT = sibling.wrapT;
        configureStableTextureSampling(aligned);
        commitTextureUV(aligned);
        safeMarkNeedsUpdate(aligned);
        createdTextures.push(aligned);
        result.anisotropyMap = aligned;
      }
    }
    if (nodeData.invertAlpha) result._invertAlpha = true;
    if (nodeData.skipToneMap === true) result.skipToneMap = true;
    if (result.map instanceof THREE.Texture && (result.color == null || result.color === '')) {
      result.color = '#ffffff';
    }
    // Absorb Dist ≤ 0 means "auto / off" — never emit 0 (Three.js treats 0 as divide-by-zero).
    if (
      typeof result.attenuationDistance === 'number' &&
      (!Number.isFinite(result.attenuationDistance) || result.attenuationDistance <= 0)
    ) {
      delete result.attenuationDistance;
    }
    // Default white Absorb Color is a no-op; omit so auto can derive from base color.
    if (
      typeof result.attenuationColor === 'string' &&
      /^#fff(fff)?$/i.test(result.attenuationColor.trim())
    ) {
      delete result.attenuationColor;
    }
    const envPreset = nodeData.envOverridePreset as string | undefined;
    const envUrl = nodeData.envOverrideUrl as string | undefined;
    const envRotDeg = (nodeData.envRotation as number) ?? 0;
    const envLink = !!nodeData.envLinkCamera;
    if (envPreset === '__custom__' && envUrl) {
      result._envOverride = { url: envUrl, rotation: envRotDeg, linkCamera: envLink };
    } else if (envPreset && envPreset !== '__custom__') {
      result._envOverride = { preset: envPreset, rotation: envRotDeg, linkCamera: envLink };
    }
    // Always emit explicit coat / anisotropy fields. Omitted keys made the player keep the previous
    // material's clearcoat / anisotropy (Carpaint→Metal Mesh looked lacquered; Carbon→Wood kept weave).
    ensureExplicitCoatFields(result);
    // After coerce: a map with scalar 0 disables USE_ANISOTROPY in Three.js entirely.
    if (
      !nodeData.disabled_anisotropy &&
      result.anisotropyMap instanceof THREE.Texture &&
      (!(typeof result.anisotropy === 'number') || result.anisotropy <= 0)
    ) {
      result.anisotropy = 1;
    }
    if (typeof result.roughness !== 'number' || !Number.isFinite(result.roughness)) {
      result.roughness =
        typeof nodeData.default_roughness === 'number' ? nodeData.default_roughness : 0.5;
    }
    if (typeof result.metalness !== 'number' || !Number.isFinite(result.metalness)) {
      result.metalness =
        typeof nodeData.default_metalness === 'number' ? nodeData.default_metalness : 0;
    }
    if (typeof result.envMapIntensity !== 'number' || !Number.isFinite(result.envMapIntensity)) {
      result.envMapIntensity = 1;
    }
    return result;
  }

  // ── Determine root: explicit exit → Material Blender (legacy) → first PBR Output ──
  type ResolveBlenderFn = (blenderNode: MaterialGraphNode) => Record<string, any>;

  const resolveBlender: ResolveBlenderFn = (blenderNode) => {
    const findPBR = (handle: string): MaterialGraphNode | undefined => {
      const edge = graph.edges.find((e) => e.target === blenderNode.id && e.targetHandle === handle);
      if (!edge) return undefined;
      const srcNode = nodeMap.get(edge.source);
      if (srcNode?.type === 'pbrOutput' && !nodeBranchDisabled(srcNode)) return srcNode;
      return undefined;
    };

    let blenderOverrides: Record<string, any> = {};
    const basePBR = findPBR('base');
    if (basePBR) {
      blenderOverrides = resolvePBR(basePBR);
    }

    const blenderData = (blenderNode.data as Record<string, any>) ?? {};
    const layers: Array<{
      mask: THREE.Texture;
      maskUvChannel: number;
      color: string;
      map: THREE.Texture | null;
      mapUvChannel: number;
      normalMap: THREE.Texture | null;
      normalUvChannel: number;
      bumpMap: THREE.Texture | null;
      bumpUvChannel: number;
      bumpScale: number;
      roughness: number;
      roughnessMap: THREE.Texture | null;
      roughnessUvChannel: number;
      metalness: number;
      metalnessMap: THREE.Texture | null;
      metalnessUvChannel: number;
      amount: number;
    }> = [];

    for (const layerKey of ['layer1', 'layer2'] as const) {
      const layerPBR = findPBR(layerKey);
      if (!layerPBR) continue;

      const maskEdge = graph.edges.find((e) => e.target === blenderNode.id && e.targetHandle === `${layerKey}_mask`);
      const maskRaw = maskEdge ? outputs.get(maskEdge.source)?.get(maskEdge.sourceHandle) : undefined;
      const maskTex =
        maskRaw instanceof THREE.Texture ? maskRaw : getFullMaskTexture();

      const resolved = resolvePBR(layerPBR);
      const amount = typeof blenderData[`${layerKey}_amount`] === 'number' ? blenderData[`${layerKey}_amount`] as number : 1;
      const maskUvChannel = typeof blenderData[`${layerKey}_maskUv`] === 'number' ? blenderData[`${layerKey}_maskUv`] as number : 0;

      const mapTex = resolved.map instanceof THREE.Texture ? resolved.map : null;
      const normalTex = resolved.normalMap instanceof THREE.Texture ? resolved.normalMap : null;
      const bumpTex = resolved.bumpMap instanceof THREE.Texture ? resolved.bumpMap : null;
      const roughTex = resolved.roughnessMap instanceof THREE.Texture ? resolved.roughnessMap : null;
      const metalTex = resolved.metalnessMap instanceof THREE.Texture ? resolved.metalnessMap : null;

      layers.push({
        mask: maskTex,
        maskUvChannel,
        color: typeof resolved.color === 'string' ? resolved.color : '#ffffff',
        map: mapTex,
        mapUvChannel: mapTex ? (mapTex.channel ?? 0) : 0,
        normalMap: normalTex,
        normalUvChannel: normalTex ? (normalTex.channel ?? 0) : 0,
        bumpMap: bumpTex,
        bumpUvChannel: bumpTex ? (bumpTex.channel ?? 0) : 0,
        bumpScale: typeof resolved.bumpScale === 'number' ? resolved.bumpScale : 1,
        roughness: typeof resolved.roughness === 'number' ? resolved.roughness : 0.5,
        roughnessMap: roughTex,
        roughnessUvChannel: roughTex ? (roughTex.channel ?? 0) : 0,
        metalness: typeof resolved.metalness === 'number' ? resolved.metalness : 0,
        metalnessMap: metalTex,
        metalnessUvChannel: metalTex ? (metalTex.channel ?? 0) : 0,
        amount,
      });
    }

    if (layers.length > 0) {
      blenderOverrides._materialLayers = layers;
    }

    if (!basePBR && layers.length === 0) {
      const orphan = graph.nodes.find((n) => n.type === 'pbrOutput' && !nodeBranchDisabled(n));
      if (orphan) blenderOverrides = resolvePBR(orphan);
    }

    ensureExplicitCoatFields(blenderOverrides);
    return blenderOverrides;
  };

  const resolveExitNode = (exitNode: MaterialGraphNode): Record<string, any> | null => {
    if (nodeBranchDisabled(exitNode)) return null;
    if (exitNode.type === 'pbrOutput') return resolvePBR(exitNode);
    if (exitNode.type === 'materialBlender') return resolveBlender(exitNode);
    return null;
  };

  /** Flat material for inspecting a single node's output on the mesh (UV / texture debug). */
  const buildDebugPreviewOverrides = (
    nodeOutputs: Map<string, unknown> | undefined,
    debugNode?: MaterialGraphNode,
  ): Record<string, unknown> | null => {
    if (!nodeOutputs && !debugNode) return null;

    const texture =
      nodeOutputs?.get('texture') ??
      nodeOutputs?.get('bump') ??
      nodeOutputs?.get('opacity');

    const baseDebug = {
      _debugPreview: true,
      color: '#ffffff',
      metalness: 0,
      roughness: 1,
      envMapIntensity: 0,
      transmission: 0,
      thickness: 0,
      opacity: 1,
      clearcoat: 0,
      clearcoatRoughness: 0,
      emissive: '#000000',
      emissiveIntensity: 0,
      ior: 1.5,
      // Explicit nulls so applied materials wipe prior graph / GLB maps.
      normalMap: null,
      bumpMap: null,
      roughnessMap: null,
      metalnessMap: null,
      emissiveMap: null,
      aoMap: null,
      alphaMap: null,
      clearcoatMap: null,
      clearcoatNormalMap: null,
      clearcoatRoughnessMap: null,
      anisotropyMap: null,
      displacementMap: null,
    };

    if (texture instanceof THREE.Texture) {
      promotePendingCpuProcessTexture(texture);
      const srcForPreview =
        graphTextureReadyForGpu(texture)
          ? texture
          : ((texture.userData as Record<string, unknown> | undefined)?.__c3dBakeStandIn as
              | THREE.Texture
              | undefined) ?? texture;
      // Preview as albedo: clone + sRGB so Linear (normal-map) textures still show as an image.
      const preview = cloneTextureLight(srcForPreview);
      preview.colorSpace = THREE.SRGBColorSpace;
      preview.needsUpdate = true;
      createdTextures.push(preview);
      return { ...baseDebug, map: preview };
    }

    // Fallback: Image node with a URL but texture not in outputs yet.
    if (debugNode?.type === 'imageTexture') {
      const url = typeof debugNode.data?.url === 'string' ? debugNode.data.url.trim() : '';
      if (url) {
        const tex = loadTex(url, false);
        return { ...baseDebug, map: tex };
      }
    }

    const color = nodeOutputs?.get('color');
    if (typeof color === 'string') {
      return { ...baseDebug, color, map: null, roughness: 0.45, envMapIntensity: 0.2 };
    }

    const value = nodeOutputs?.get('value');
    if (typeof value === 'number' && Number.isFinite(value)) {
      const t = Math.max(0, Math.min(1, value));
      const tex = createSolidColorTexture(t * 255, t * 255, t * 255);
      createdTextures.push(tex);
      return { ...baseDebug, map: tex };
    }

    // Attribute / text often produce strings — show as flat color if they look like hex.
    if (typeof value === 'string' && value.trim()) {
      const s = value.trim();
      if (s.startsWith('#') || s.startsWith('rgb')) {
        return { ...baseDebug, color: s, map: null, roughness: 0.45, envMapIntensity: 0.2 };
      }
    }

    return null;
  };

  let overrides: Record<string, any> = {};

  const debugId = evalContext?.allowDebugPreview ? graph.debugPreviewNodeId : undefined;
  if (debugId) {
    const fromDebug = buildDebugPreviewOverrides(outputs.get(debugId), nodeMap.get(debugId));
    if (fromDebug) overrides = fromDebug;
  }

  if (Object.keys(overrides).length === 0) {
    const explicitExit = graph.activeOutputId ? nodeMap.get(graph.activeOutputId) : undefined;
    if (isGraphExitNode(explicitExit)) {
      const fromExit = resolveExitNode(explicitExit);
      if (fromExit) {
        overrides = fromExit;
      }
    }
  }

  if (Object.keys(overrides).length === 0) {
    const blenderNode = graph.nodes.find((n) => n.type === 'materialBlender' && !nodeBranchDisabled(n));
    if (blenderNode) {
      overrides = resolveBlender(blenderNode);
    } else {
      const pbrNode = graph.nodes.find((n) => n.type === 'pbrOutput' && !nodeBranchDisabled(n));
      if (pbrNode) {
        overrides = resolvePBR(pbrNode);
      }
    }
  }

  // Stamp UV Randomizer ranges on overrides immediately (before textures decode) so
  // array instance UV can latch ranges on first apply without a late UV flash.
  if (overrides && !overrides[C3D_UV_RANDOMIZE_UD]) {
    const uvNode = graph.nodes.find((n) => n.type === 'uvRandomize' && !nodeMuted(n) && !nodeBranchDisabled(n));
    if (uvNode) {
      const d = (uvNode.data ?? {}) as Record<string, unknown>;
      overrides[C3D_UV_RANDOMIZE_UD] = buildUvRandomizeMetaFromNodeData(
        d,
        !!d.perMesh,
        Number(d.seed) || 0,
      );
    }
  }

  stampSkipToneMapFromLibraryName(overrides, evalContext?.libraryName);

  const evalResult = { overrides, textures: createdTextures };
  if (evalCache.size >= MAX_EVAL_CACHE) {
    const first = evalCache.keys().next().value;
    if (first !== undefined) evalCache.delete(first);
  }
  evalCache.set(storageKey, evalResult);
  return evalResult;
}

function clamp255(v: number): number {
  return Math.min(255, Math.max(0, v));
}

/** Copy UV Transform fields (Tile / Offset / Rot / wrap) from one texture onto another. */
function adoptTextureUv(target: THREE.Texture, source: THREE.Texture): void {
  target.repeat.copy(source.repeat);
  target.offset.copy(source.offset);
  target.center.copy(source.center);
  target.rotation = source.rotation;
  target.wrapS = source.wrapS;
  target.wrapT = source.wrapT;
  commitTextureUV(target);
  safeMarkNeedsUpdate(target);
}

/** True when texture UV is still at defaults (not routed through UV Transform). */
function textureUvIsIdentity(tex: THREE.Texture): boolean {
  return (
    Math.abs(tex.repeat.x - 1) < 1e-6 &&
    Math.abs(tex.repeat.y - 1) < 1e-6 &&
    Math.abs(tex.offset.x) < 1e-6 &&
    Math.abs(tex.offset.y) < 1e-6 &&
    Math.abs(tex.rotation) < 1e-6
  );
}

function parseHexRgb(s: string): { r: number; g: number; b: number } | null {
  const t = String(s).trim();
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(t);
  if (!m) return null;
  const h = m[1];
  if (h.length === 3) {
    return {
      r: parseInt(h[0] + h[0], 16),
      g: parseInt(h[1] + h[1], 16),
      b: parseInt(h[2] + h[2], 16),
    };
  }
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function formatHexRgb(r: number, g: number, b: number): string {
  const q = (x: number) => clamp255(Math.round(x)).toString(16).padStart(2, '0');
  return `#${q(r)}${q(g)}${q(b)}`;
}

/** Hex (#rgb / #rrggbb), or CSS-style strings (e.g. `blue`, `rgb(...)`) via THREE.Color. */
function rgbFromColorString(s: string): { r: number; g: number; b: number } | null {
  const hex = parseHexRgb(s);
  if (hex) return hex;
  try {
    const c = new THREE.Color();
    c.set(String(s).trim());
    return { r: Math.round(c.r * 255), g: Math.round(c.g * 255), b: Math.round(c.b * 255) };
  } catch {
    return null;
  }
}

function createSolidColorTexture(r: number, g: number, b: number): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  const R = clamp255(Math.round(r));
  const G = clamp255(Math.round(g));
  const B = clamp255(Math.round(b));
  ctx.fillStyle = `rgb(${R},${G},${B})`;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.flipY = false;
  // Lets PBR Color socket keep a string tint (lerpable) instead of a map swap.
  (tex.userData as any).__c3dSolidColor = formatHexRgb(R, G, B);
  tex.needsUpdate = true;
  return tex;
}

/** Adjust wired color string: multiply RGB, then add lightness offset. */
function adjustWiredHexColor(input: string, multiply: number, lightness: number): string {
  const parsed = rgbFromColorString(input);
  if (!parsed) return '#ffffff';
  const m = Number.isFinite(multiply) ? multiply : 1;
  const L = Number.isFinite(lightness) ? lightness : 0;
  const add = L * 255;
  return formatHexRgb(parsed.r * m + add, parsed.g * m + add, parsed.b * m + add);
}

function hashStringFNV1a(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h | 0;
}

export function combineUvRandomizeSeed(baseSeed: number, meshKey: string): number {
  const tag = hashStringFNV1a(meshKey);
  return (Math.imul(baseSeed | 0, 0x27d4eb2d) ^ tag) >>> 0;
}

/** Deterministic [0,1) floats from an integer seed (stable across evals / platforms). */
export function createSeededRng(seed: number): () => number {
  let state = Math.imul(Math.floor(seed) | 0, 0x9e3779b1) || 0x12345678;
  return (): number => {
    state = (state + 0x6d2b79f5) | 0;
    let z = state;
    z = Math.imul(z ^ (z >>> 15), z | 1);
    z ^= z + Math.imul(z ^ (z >>> 7), z | 61);
    return ((z ^ (z >>> 14)) >>> 0) / 4294967296;
  };
}

function isImageReady(img: unknown): img is CanvasImageSource {
  if (!img) return false;
  if (img instanceof HTMLImageElement) return img.complete && img.naturalWidth > 0;
  if (img instanceof HTMLVideoElement) return img.readyState >= 2;
  if (img instanceof HTMLCanvasElement || img instanceof OffscreenCanvas || img instanceof ImageBitmap) return true;
  return true;
}

/**
 * UV-identity bake clones may be created before TextureLoader fills `image`. Adopt it from
 * `__c3dCloneSource` (same as GlbModel) so chained CPU nodes don't sit on a null image forever.
 */
function adoptCloneSourceImage(tex: THREE.Texture): void {
  const ud = tex.userData as Record<string, unknown> | undefined;
  if (tex.image || !ud?.__c3dCloneSource) return;
  const src = ud.__c3dCloneSource as THREE.Texture;
  if (src.image) {
    tex.image = src.image;
    delete ud.__c3dCloneSource;
    tex.needsUpdate = true;
  }
}

/** True when a texture's canvas/image is safe to sample (not a white CPU-bake placeholder). */
function textureCpuPixelsSettled(tex: THREE.Texture | null | undefined): boolean {
  if (!tex) return false;
  adoptCloneSourceImage(tex);
  if (!tex.image) return false;
  if ((tex.userData as Record<string, unknown> | undefined)?.__c3dBakePending) return false;
  return isImageReady(tex.image);
}

/** Bump when CPU pixels change so downstream processTexture polls re-bake. */
function bumpCpuContentEpoch(tex: THREE.Texture): void {
  const ud = tex.userData as Record<string, unknown>;
  ud.__c3dContentEpoch = ((ud.__c3dContentEpoch as number) || 0) + 1;
}

function cpuContentEpoch(tex: THREE.Texture | null | undefined): number {
  if (!tex) return 0;
  return ((tex.userData as Record<string, unknown> | undefined)?.__c3dContentEpoch as number) || 0;
}

/**
 * Epoch of the pixels we actually sample — walk clones / CPU-chain inputs.
 * A fresh UV clone often has epoch 0 while a prior bake stored epoch 1; comparing
 * only the clone made Wood HSL/roughness rebake (and flicker) on every select.
 */
function sourceContentEpoch(tex: THREE.Texture | null | undefined, seen?: Set<string>): number {
  if (!tex) return 0;
  const visiting = seen ?? new Set<string>();
  if (visiting.has(tex.uuid)) return cpuContentEpoch(tex);
  visiting.add(tex.uuid);
  const ud = tex.userData as Record<string, unknown> | undefined;
  const from =
    (ud?.__c3dCloneSource as THREE.Texture | undefined) ||
    (ud?.__c3dProcessInput as THREE.Texture | undefined) ||
    (ud?.__c3dBakeSource as THREE.Texture | undefined) ||
    (ud?.__c3dProcessSource as THREE.Texture | undefined);
  if (from instanceof THREE.Texture && from !== tex) {
    return Math.max(cpuContentEpoch(tex), sourceContentEpoch(from, visiting));
  }
  return cpuContentEpoch(tex);
}

const _processTexUvScratch = new THREE.Vector2();

function textureNeedsUvAwareSampling(tex: THREE.Texture): boolean {
  const rx = tex.repeat?.x ?? 1;
  const ry = tex.repeat?.y ?? 1;
  const ox = tex.offset?.x ?? 0;
  const oy = tex.offset?.y ?? 0;
  const cx = tex.center?.x ?? 0;
  const cy = tex.center?.y ?? 0;
  return (
    Math.abs(rx - 1) > 1e-5 ||
    Math.abs(ry - 1) > 1e-5 ||
    Math.abs(ox) > 1e-5 ||
    Math.abs(oy) > 1e-5 ||
    Math.abs(tex.rotation ?? 0) > 1e-5 ||
    Math.abs(cx) > 1e-5 ||
    Math.abs(cy) > 1e-5 ||
    tex.wrapS !== THREE.ClampToEdgeWrapping ||
    tex.wrapT !== THREE.ClampToEdgeWrapping
  );
}

function getCanvasImageDimensions(img: CanvasImageSource): { w: number; h: number } {
  if (img instanceof HTMLImageElement) return { w: img.naturalWidth, h: img.naturalHeight };
  if (img instanceof HTMLVideoElement) return { w: img.videoWidth, h: img.videoHeight };
  if (img instanceof HTMLCanvasElement || img instanceof OffscreenCanvas) return { w: img.width, h: img.height };
  if (typeof ImageBitmap !== 'undefined' && img instanceof ImageBitmap) return { w: img.width, h: img.height };
  return { w: 512, h: 512 };
}

/** Raster for CPU sampling; caps resolution to keep graph eval responsive. */
function getTextureRasterForSampling(tex: THREE.Texture): { data: Uint8ClampedArray; w: number; h: number } | null {
  const imgUnknown = tex.image as unknown;
  if (!imgUnknown) return null;

  const maybeData = imgUnknown as { data?: ArrayBufferView; width?: number; height?: number };
  if (maybeData.data != null && typeof maybeData.width === 'number' && typeof maybeData.height === 'number') {
    const w = Math.max(1, maybeData.width);
    const h = Math.max(1, maybeData.height);
    const raw = maybeData.data;
    if (raw instanceof Uint8ClampedArray && raw.length >= w * h * 4) {
      return { data: raw, w, h };
    }
    if (raw instanceof Uint8Array && raw.byteLength >= w * h * 4) {
      return { data: new Uint8ClampedArray(raw.buffer, raw.byteOffset, w * h * 4), w, h };
    }
  }

  if (!isImageReady(tex.image)) return null;
  const src = tex.image as CanvasImageSource;
  const dim = getCanvasImageDimensions(src);
  const fit = fitCpuBakeDims(dim.w, dim.h, graphCpuTextureSizeCap());
  const w = fit.w;
  const h = fit.h;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const x = c.getContext('2d', { willReadFrequently: true });
  if (!x) return null;
  try {
    x.drawImage(src, 0, 0, w, h);
  } catch {
    return null;
  }
  return { data: x.getImageData(0, 0, w, h).data, w, h };
}

function sampleTextureBilinear(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  u: number,
  v: number,
): [number, number, number, number] {
  const x = Math.min(w - 1, Math.max(0, u * (w - 1)));
  const y = Math.min(h - 1, Math.max(0, (1 - v) * (h - 1)));
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(x0 + 1, w - 1);
  const y1 = Math.min(y0 + 1, h - 1);
  const tx = x - x0;
  const ty = y - y0;
  const idx = (xx: number, yy: number) => (yy * w + xx) * 4;
  const i00 = idx(x0, y0);
  const i10 = idx(x1, y0);
  const i01 = idx(x0, y1);
  const i11 = idx(x1, y1);
  const r =
    data[i00] * (1 - tx) * (1 - ty) +
    data[i10] * tx * (1 - ty) +
    data[i01] * (1 - tx) * ty +
    data[i11] * tx * ty;
  const g =
    data[i00 + 1] * (1 - tx) * (1 - ty) +
    data[i10 + 1] * tx * (1 - ty) +
    data[i01 + 1] * (1 - tx) * ty +
    data[i11 + 1] * tx * ty;
  const b =
    data[i00 + 2] * (1 - tx) * (1 - ty) +
    data[i10 + 2] * tx * (1 - ty) +
    data[i01 + 2] * (1 - tx) * ty +
    data[i11 + 2] * tx * ty;
  const a =
    data[i00 + 3] * (1 - tx) * (1 - ty) +
    data[i10 + 3] * tx * (1 - ty) +
    data[i01 + 3] * (1 - tx) * ty +
    data[i11 + 3] * tx * ty;
  return [r, g, b, a];
}

/** Bakes `tex` into a size×size ImageData (UV-aware when repeat/offset/wrap require it). */
// --- Cooperative time-slicing for CPU texture processing ---------------------
// The per-pixel passes below (UV-aware bake, separable blur, brightness/contrast,
// height-to-normal, masked blend) can each touch ~1M pixels. Running them
// synchronously produced long "message" / "setInterval" / "requestAnimationFrame"
// violations on load and on every configuration change. We now run them in short
// time-budgeted slices, yielding to the event loop between row bands so no single
// task blocks the main thread. Output is identical to the old synchronous path:
// the same loops run in the same raster order over the same buffers — only the
// scheduling changes.
const CPU_TEXTURE_SLICE_BUDGET_MS = 6;

const nowMs: () => number =
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? () => performance.now()
    : () => Date.now();

/** Yields control to the event loop as a macrotask (MessageChannel is faster than setTimeout(0)). */
const yieldToScheduler: () => Promise<void> = (() => {
  if (typeof MessageChannel !== 'undefined') {
    const channel = new MessageChannel();
    const waiters: Array<() => void> = [];
    channel.port1.onmessage = () => {
      waiters.shift()?.();
    };
    return () =>
      new Promise<void>((resolve) => {
        waiters.push(resolve);
        channel.port2.postMessage(0);
      });
  }
  return () => new Promise<void>((resolve) => setTimeout(resolve, 0));
})();

/**
 * Runs `rowFn(y)` for `y` in `[0, height)` in raster order, yielding whenever a
 * slice exceeds the time budget. Returns `false` if `shouldCancel()` flipped (a
 * newer draw superseded this one) — the caller must then discard its result.
 */
async function runRowsBudgeted(
  height: number,
  rowFn: (y: number) => void,
  shouldCancel: () => boolean,
): Promise<boolean> {
  let sliceStart = nowMs();
  for (let y = 0; y < height; y++) {
    if (shouldCancel()) return false;
    rowFn(y);
    if (nowMs() - sliceStart >= CPU_TEXTURE_SLICE_BUDGET_MS) {
      await yieldToScheduler();
      if (shouldCancel()) return false;
      sliceStart = nowMs();
    }
  }
  return true;
}

/**
 * Replaces the old "sync initial draw + 100ms×300 poll that re-runs draw()"
 * pattern shared by the CPU texture processors. Draws run asynchronously and are
 * token-guarded so a newer draw cancels an in-flight one, preventing overlapping
 * writes to the shared canvas. `draw` resolves to `true` once it has committed
 * pixels to the canvas.
 */
function installChunkedDraw(opts: {
  sourceReady: () => boolean;
  currentKey: () => string | number;
  draw: (shouldCancel: () => boolean) => Promise<boolean>;
  /** Optional: receive the kick fn so callers can force a redraw (stale CPU chain). */
  attachKick?: (kick: () => void) => void;
}): void {
  let lastDrawnKey: string | number | null = null;
  let token = 0;
  let running = false;
  let counted = false;

  const endCounted = () => {
    if (!counted) return;
    counted = false;
    bumpActiveChunkedDraws(-1);
  };

  const kick = () => {
    const myToken = ++token;
    const key = opts.currentKey();
    running = true;
    if (!counted) {
      counted = true;
      bumpActiveChunkedDraws(1);
    }
    opts
      .draw(() => myToken !== token)
      .then((drawn) => {
        if (myToken !== token) return; // superseded by a newer draw
        running = false;
        if (drawn) {
          lastDrawnKey = key;
          if (typeof document !== 'undefined') {
            document.dispatchEvent(new CustomEvent('scene-texture-ready'));
          }
        }
        endCounted();
      })
      .catch(() => {
        if (myToken !== token) return;
        running = false;
        endCounted();
      });
  };

  opts.attachKick?.(kick);

  if (opts.sourceReady()) kick();

  let ticks = 0;
  const maxTicks = 600; // ~60s — player cold-start can chain CPU bakes behind slow image loads
  const onTexReady = () => {
    if (opts.sourceReady() && !running && opts.currentKey() !== lastDrawnKey) {
      kick();
    }
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('scene-texture-ready', onTexReady);
  }
  const poll = setInterval(() => {
    ticks++;
    onTexReady();
    if (ticks >= maxTicks) {
      clearInterval(poll);
      if (typeof document !== 'undefined') {
        document.removeEventListener('scene-texture-ready', onTexReady);
      }
      // Safety: don't leave the reveal gate stuck if a draw never settles.
      endCounted();
    }
  }, 100);
}

/**
 * Offscreen blit target. `processTexture` result canvases are often already bound as
 * albedo/roughness — drawing the raw source onto that same `ctx` showed the unprocessed
 * look for the whole budgeted bake (Carpaint darker, Wood glossy) then snapped back.
 */
let _cpuBakeScratch: HTMLCanvasElement | null = null;

function getCpuBakeScratchCtx(w: number, h: number): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  if (!_cpuBakeScratch) _cpuBakeScratch = document.createElement('canvas');
  if (_cpuBakeScratch.width !== w || _cpuBakeScratch.height !== h) {
    _cpuBakeScratch.width = w;
    _cpuBakeScratch.height = h;
  }
  return _cpuBakeScratch.getContext('2d', { willReadFrequently: true });
}

function blitTextureToImageData(tex: THREE.Texture, w: number, h: number): ImageData | null {
  const sctx = getCpuBakeScratchCtx(w, h);
  if (!sctx) return null;
  sctx.fillStyle = '#ffffff';
  sctx.fillRect(0, 0, w, h);
  if (isImageReady(tex.image)) {
    sctx.drawImage(tex.image as CanvasImageSource, 0, 0, w, h);
  }
  return sctx.getImageData(0, 0, w, h);
}

async function bakeTextureToImageData(
  tex: THREE.Texture,
  w: number,
  h: number,
  ctx: CanvasRenderingContext2D,
  shouldCancel: () => boolean = () => false,
): Promise<ImageData | null> {
  adoptCloneSourceImage(tex);
  if (!isImageReady(tex.image)) return null;
  tex.updateMatrix();

  if (textureNeedsUvAwareSampling(tex)) {
    const raster = getTextureRasterForSampling(tex);
    if (raster) {
      const imgData = ctx.createImageData(w, h);
      const d = imgData.data;
      const { data: srcData, w: iw, h: ih } = raster;
      const ok = await runRowsBudgeted(
        h,
        (j) => {
          for (let i = 0; i < w; i++) {
            _processTexUvScratch.set((i + 0.5) / w, 1 - (j + 0.5) / h);
            tex.transformUv(_processTexUvScratch);
            const [r, g, b, a] = sampleTextureBilinear(
              srcData,
              iw,
              ih,
              _processTexUvScratch.x,
              _processTexUvScratch.y,
            );
            const o = (j * w + i) * 4;
            d[o] = r;
            d[o + 1] = g;
            d[o + 2] = b;
            d[o + 3] = a;
          }
        },
        shouldCancel,
      );
      if (!ok) return null;
      return imgData;
    }
  }

  return blitTextureToImageData(tex, w, h);
}

/** Sync sample — used only when applying a pending Exposure/HSL map to a live mesh. */
function bakeTextureToImageDataSync(
  tex: THREE.Texture,
  w: number,
  h: number,
  _ctx: CanvasRenderingContext2D,
): ImageData | null {
  adoptCloneSourceImage(tex);
  if (!isImageReady(tex.image)) return null;
  tex.updateMatrix();

  if (textureNeedsUvAwareSampling(tex)) {
    const raster = getTextureRasterForSampling(tex);
    if (raster) {
      const imgData = _ctx.createImageData(w, h);
      const d = imgData.data;
      const { data: srcData, w: iw, h: ih } = raster;
      for (let j = 0; j < h; j++) {
        for (let i = 0; i < w; i++) {
          _processTexUvScratch.set((i + 0.5) / w, 1 - (j + 0.5) / h);
          tex.transformUv(_processTexUvScratch);
          const [r, g, b, a] = sampleTextureBilinear(
            srcData,
            iw,
            ih,
            _processTexUvScratch.x,
            _processTexUvScratch.y,
          );
          const o = (j * w + i) * 4;
          d[o] = r;
          d[o + 1] = g;
          d[o + 2] = b;
          d[o + 3] = a;
        }
      }
      return imgData;
    }
  }

  return blitTextureToImageData(tex, w, h);
}

type ProcessPixelFn = (r: number, g: number, b: number, a: number) => [number, number, number, number];

function trySyncFinishProcessTexture(result: THREE.CanvasTexture): boolean {
  const ud = result.userData as Record<string, unknown>;
  if (!ud.__c3dBakePending) return true;
  const fn = ud.__c3dProcessFn as ProcessPixelFn | undefined;
  const src = ud.__c3dProcessSource as THREE.Texture | undefined;
  // Graph input (may be another CPU node). Never sample upstream white placeholders.
  const input = (ud.__c3dProcessInput as THREE.Texture | undefined) ?? src;
  if (!fn || !src || !input) return false;
  if (input !== result) {
    promotePendingCpuProcessTexture(input);
  }
  if (!textureCpuPixelsSettled(input)) return false;
  adoptCloneSourceImage(src);
  if (input.image && !src.image) src.image = input.image;
  if (!isImageReady(src.image)) return false;
  const canvas = result.image as HTMLCanvasElement | undefined;
  if (!canvas || typeof canvas.getContext !== 'function') return false;
  const dims = graphCpuBakeDimsFor(input);
  const ctx = ensureCpuBakeCanvasSize(canvas, dims.w, dims.h);
  if (!ctx) return false;
  const imgData = bakeTextureToImageDataSync(src, dims.w, dims.h, ctx);
  if (!imgData) return false;
  const d = imgData.data;
  for (let i = 0; i < d.length; i += 4) {
    const [r, g, b, a] = fn(d[i]!, d[i + 1]!, d[i + 2]!, d[i + 3]!);
    d[i] = clamp255(r);
    d[i + 1] = clamp255(g);
    d[i + 2] = clamp255(b);
    d[i + 3] = clamp255(a);
  }
  ctx.putImageData(imgData, 0, 0);
  delete ud.__c3dBakePending;
  delete ud.__c3dBakeStandIn;
  bumpCpuContentEpoch(result);
  const inputForEpoch = (ud.__c3dProcessInput as THREE.Texture | undefined) ?? src;
  ud.__c3dBakedFromEpoch = cpuContentEpoch(inputForEpoch);
  const adoptFrom = ud.__c3dProcessUvAdoptFrom as THREE.Texture | undefined;
  // Only adopt identity/source UV onto the actual CPU-bake canvas — never onto a
  // UV Transform / Randomize clone that pointed here via shared userData (legacy).
  const looksLikeUvClone =
    Math.abs((result.repeat?.x ?? 1) - 1) > 1e-5 ||
    Math.abs((result.repeat?.y ?? 1) - 1) > 1e-5 ||
    Math.abs(result.offset?.x ?? 0) > 1e-5 ||
    Math.abs(result.offset?.y ?? 0) > 1e-5 ||
    Math.abs(result.rotation ?? 0) > 1e-5;
  if (!looksLikeUvClone) {
    if (adoptFrom) adoptTextureUv(result, adoptFrom);
    else resetTextureUvIdentity(result);
  }
  result.needsUpdate = true;
  if (typeof document !== 'undefined') {
    document.dispatchEvent(new CustomEvent('scene-texture-ready'));
  }
  return true;
}

/**
 * Promote a pending CPU-processed map (or UV clone of one) when applying to a live mesh.
 * Graph eval stays async (no load-time stall); only textures about to hit the GPU finish sync.
 * Returns true when `tex` is no longer bake-pending.
 */
export function promotePendingCpuProcessTexture(tex: THREE.Texture): boolean {
  const ud = tex.userData as Record<string, unknown>;
  if (!ud.__c3dBakePending) return true;

  // UV Transform / Randomizer clones point at the pending canvas — finish that first.
  const bakeSource = ud.__c3dBakeSource as THREE.Texture | undefined;
  if (bakeSource) {
    if (!promotePendingCpuProcessTexture(bakeSource)) return false;
    delete ud.__c3dBakePending;
    delete ud.__c3dBakeSource;
    delete ud.__c3dBakeStandIn;
    // UV Transform / Randomize may clone the placeholder canvas from a pending
    // CPU node (Brightness / Exposure / HSL). Once the source finishes, always
    // swap in the resolved image; otherwise the clone can stay stuck on the
    // white stand-in because it already had an `image` assigned.
    if (bakeSource.image) tex.image = bakeSource.image;
    tex.needsUpdate = true;
    return true;
  }

  // Blend / custom bakers store a sync finisher (pixel recipes use __c3dProcessFn below).
  const syncFinish = ud.__c3dSyncFinish as (() => boolean) | undefined;
  if (typeof syncFinish === 'function') {
    return syncFinish();
  }

  if (ud.__c3dCpuProcessCached) {
    return trySyncFinishProcessTexture(tex as THREE.CanvasTexture);
  }
  return false;
}

/**
 * True when a graph-produced texture is safe to bind on a material (not a white CPU
 * placeholder, source image decoded). Always runs {@link promotePendingCpuProcessTexture} first.
 */
export function graphTextureReadyForGpu(tex: THREE.Texture): boolean {
  promotePendingCpuProcessTexture(tex);
  const ud = tex.userData as Record<string, unknown> | undefined;
  if (ud?.__c3dBakePending) return false;
  if (!isImageReady(tex.image)) return false;
  const im = tex.image as
    | HTMLImageElement
    | ImageBitmap
    | { width?: number; height?: number; videoWidth?: number; videoHeight?: number }
    | null
    | undefined;
  if (im == null) return false;
  if (typeof HTMLImageElement !== 'undefined' && im instanceof HTMLImageElement) {
    return im.complete && im.naturalWidth > 0;
  }
  if (typeof ImageBitmap !== 'undefined' && im instanceof ImageBitmap) {
    return im.width > 0 && im.height > 0;
  }
  if ((tex as THREE.DataTexture).isDataTexture === true) {
    return true;
  }
  const w = (im as { width?: number; videoWidth?: number }).width ?? (im as { videoWidth?: number }).videoWidth;
  const h = (im as { height?: number; videoHeight?: number }).height ?? (im as { videoHeight?: number }).videoHeight;
  return typeof w === 'number' && typeof h === 'number' && w > 0 && h > 0;
}

type BlurSharpenOpacityChannel = 'rgb' | 'r' | 'g' | 'b' | 'a';

function parseBlurSharpenOpacityChannel(raw: unknown): BlurSharpenOpacityChannel {
  const s = typeof raw === 'string' ? raw : 'rgb';
  if (s === 'r' || s === 'g' || s === 'b' || s === 'a' || s === 'rgb') return s;
  return 'rgb';
}

function opacityFromRgbaSource(
  r: number,
  g: number,
  b: number,
  a: number,
  src: BlurSharpenOpacityChannel,
): number {
  switch (src) {
    case 'rgb':
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    case 'r':
      return r / 255;
    case 'g':
      return g / 255;
    case 'b':
      return b / 255;
    case 'a':
      return a / 255;
    default:
      return 1;
  }
}

/**
 * Separable box blur on RGB; leaves alpha bytes unchanged (still copy of source).
 * Runs in time-budgeted slices; returns `false` if a newer draw superseded this one.
 */
async function separableBoxBlurRgb(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  r: number,
  shouldCancel: () => boolean,
): Promise<boolean> {
  const n = w * h;
  const tmpR = new Float32Array(n);
  const tmpG = new Float32Array(n);
  const tmpB = new Float32Array(n);
  const pass1 = await runRowsBudgeted(
    h,
    (j) => {
      for (let i = 0; i < w; i++) {
        let sr = 0;
        let sg = 0;
        let sb = 0;
        let cnt = 0;
        for (let dx = -r; dx <= r; dx++) {
          const xi = Math.min(w - 1, Math.max(0, i + dx));
          const o = (j * w + xi) * 4;
          sr += data[o];
          sg += data[o + 1];
          sb += data[o + 2];
          cnt++;
        }
        const ti = j * w + i;
        tmpR[ti] = sr / cnt;
        tmpG[ti] = sg / cnt;
        tmpB[ti] = sb / cnt;
      }
    },
    shouldCancel,
  );
  if (!pass1) return false;

  const outR = new Float32Array(n);
  const outG = new Float32Array(n);
  const outB = new Float32Array(n);
  const pass2 = await runRowsBudgeted(
    h,
    (j) => {
      for (let i = 0; i < w; i++) {
        let sr = 0;
        let sg = 0;
        let sb = 0;
        let cnt = 0;
        for (let dy = -r; dy <= r; dy++) {
          const yj = Math.min(h - 1, Math.max(0, j + dy));
          const ti = yj * w + i;
          sr += tmpR[ti];
          sg += tmpG[ti];
          sb += tmpB[ti];
          cnt++;
        }
        const oi = j * w + i;
        outR[oi] = sr / cnt;
        outG[oi] = sg / cnt;
        outB[oi] = sb / cnt;
      }
    },
    shouldCancel,
  );
  if (!pass2) return false;

  return runRowsBudgeted(
    h,
    (j) => {
      for (let i = 0; i < w; i++) {
        const k = j * w + i;
        const o = k * 4;
        data[o] = outR[k];
        data[o + 1] = outG[k];
        data[o + 2] = outB[k];
      }
    },
    shouldCancel,
  );
}

function processBlurSharpenTexture(
  tex: THREE.Texture,
  strength: number,
  radius: number,
  opacitySource: BlurSharpenOpacityChannel,
): THREE.CanvasTexture {
  const dims = graphCpuBakeDimsFor(tex);
  const canvas = document.createElement('canvas');
  canvas.width = dims.w;
  canvas.height = dims.h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  const result = new THREE.CanvasTexture(canvas);
  result.colorSpace = tex.colorSpace;
  result.flipY = false;
  result.wrapS = tex.wrapS;
  result.wrapT = tex.wrapT;
  configureStableTextureSampling(result);

  const rad = Math.max(1, Math.min(12, Math.round(radius)));

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, dims.w, dims.h);

  async function draw(shouldCancel: () => boolean): Promise<boolean> {
    const imgData = await bakeTextureToImageData(tex, dims.w, dims.h, ctx, shouldCancel);
    if (!imgData || shouldCancel()) return false;

    if (Math.abs(strength) < 1e-8) {
      ctx.putImageData(imgData, 0, 0);
      result.needsUpdate = true;
      return true;
    }

    const w = dims.w;
    const h = dims.h;
    const orig = new Uint8ClampedArray(imgData.data);
    const blurred = new Uint8ClampedArray(orig);
    if (!(await separableBoxBlurRgb(blurred, w, h, rad, shouldCancel))) return false;

    const sBlur = strength < 0 ? Math.min(1, -strength) : 0;
    const sSharp = strength > 0 ? Math.min(3, strength) : 0;

    const d = imgData.data;
    const done = await runRowsBudgeted(
      h,
      (j) => {
        for (let i = 0; i < w; i++) {
          const o = (j * w + i) * 4;
          const or = orig[o];
          const og = orig[o + 1];
          const ob = orig[o + 2];
          const oa = orig[o + 3];
          const br = blurred[o];
          const bg = blurred[o + 1];
          const bb = blurred[o + 2];
          const mask = opacityFromRgbaSource(or, og, ob, oa, opacitySource);

          let nr = or;
          let ng = og;
          let nb = ob;
          if (sBlur > 0) {
            const t = sBlur * mask;
            nr = or + t * (br - or);
            ng = og + t * (bg - og);
            nb = ob + t * (bb - ob);
          } else if (sSharp > 0) {
            const t = sSharp * mask;
            nr = or + t * (or - br);
            ng = og + t * (og - bg);
            nb = ob + t * (ob - bb);
          }

          d[o] = clamp255(nr);
          d[o + 1] = clamp255(ng);
          d[o + 2] = clamp255(nb);
          d[o + 3] = oa;
        }
      },
      shouldCancel,
    );
    if (!done) return false;

    ctx.putImageData(imgData, 0, 0);
    result.needsUpdate = true;
    return true;
  }

  installChunkedDraw({
    sourceReady: () => isImageReady(tex.image),
    currentKey: () => tex.version,
    draw,
  });

  return result;
}

function processTextureSourceKey(tex: THREE.Texture, seen?: Set<string>): string {
  const visiting = seen ?? new Set<string>();
  if (visiting.has(tex.uuid)) return `id:${tex.uuid}`;
  visiting.add(tex.uuid);
  const ud = tex.userData as Record<string, unknown> | undefined;
  if (typeof ud?.__c3dProcessCacheKey === 'string' && ud.__c3dProcessCacheKey.length > 0) {
    return ud.__c3dProcessCacheKey;
  }
  const img = tex.image as { src?: string } | undefined;
  if (typeof img?.src === 'string' && img.src.length > 0) return `src:${img.src}`;
  const cloneSrc = ud?.__c3dCloneSource;
  if (cloneSrc instanceof THREE.Texture && cloneSrc !== tex) {
    return `clone:${processTextureSourceKey(cloneSrc, visiting)}`;
  }
  const from =
    (ud?.__c3dProcessInput as THREE.Texture | undefined) ||
    (ud?.__c3dBakeSource as THREE.Texture | undefined) ||
    (ud?.__c3dProcessSource as THREE.Texture | undefined);
  if (from instanceof THREE.Texture && from !== tex) {
    return `from:${processTextureSourceKey(from, visiting)}`;
  }
  return `id:${tex.uuid}`;
}

/**
 * Three.js has a single anisotropyMap: RG = direction, B = strength.
 * When Anisotropy (strength) and Aniso Rot/Map (direction) both have textures,
 * merge instead of letting the later port overwrite the earlier.
 */
function combineAnisotropyMaps(
  dirMap: THREE.Texture,
  strengthMap: THREE.Texture,
): THREE.CanvasTexture {
  const cacheKey = `anisoCombine:${processTextureSourceKey(dirMap)}::${processTextureSourceKey(strengthMap)}`;
  const cached = cpuProcessTexCache.get(cacheKey);
  if (cached) {
    adoptTextureUv(cached, dirMap);
    return cached;
  }

  promotePendingCpuProcessTexture(dirMap);
  promotePendingCpuProcessTexture(strengthMap);

  const dims = graphCpuBakeDimsForMany([dirMap, strengthMap]);
  const canvas = document.createElement('canvas');
  canvas.width = dims.w;
  canvas.height = dims.h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.fillStyle = '#ff80ff';
  ctx.fillRect(0, 0, dims.w, dims.h);
  const result = new THREE.CanvasTexture(canvas);
  result.colorSpace = THREE.LinearSRGBColorSpace;
  result.flipY = false;
  result.wrapS = dirMap.wrapS;
  result.wrapT = dirMap.wrapT;
  result.repeat.copy(dirMap.repeat);
  result.offset.copy(dirMap.offset);
  result.rotation = dirMap.rotation;
  result.center.copy(dirMap.center);
  configureStableTextureSampling(result);
  (result.userData as any).__c3dBakePending = true;
  (result.userData as any).__c3dCpuProcessCached = true;
  cpuProcessTexCache.set(cacheKey, result);

  const syncFinish = (): boolean => {
    promotePendingCpuProcessTexture(dirMap);
    promotePendingCpuProcessTexture(strengthMap);
    if (!isImageReady(dirMap.image) || !isImageReady(strengthMap.image)) return false;
    const dirData = bakeTextureToImageDataSync(dirMap, dims.w, dims.h, ctx);
    if (!dirData) return false;
    const scratch = document.createElement('canvas');
    scratch.width = dims.w;
    scratch.height = dims.h;
    const sctx = scratch.getContext('2d', { willReadFrequently: true });
    if (!sctx) return false;
    const strData = bakeTextureToImageDataSync(strengthMap, dims.w, dims.h, sctx);
    if (!strData) return false;
    const d = dirData.data;
    const s = strData.data;
    for (let i = 0; i < d.length; i += 4) {
      // Keep direction RG from dir; strength in B from strength map (already packed or raw lum).
      d[i + 2] = s[i + 2]!;
    }
    ctx.putImageData(dirData, 0, 0);
    delete (result.userData as any).__c3dBakePending;
    result.needsUpdate = true;
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('scene-texture-ready'));
    }
    return true;
  };
  (result.userData as any).__c3dSyncFinish = syncFinish;
  // Finish immediately when sources are ready (live mesh apply promotes again if needed).
  syncFinish();

  return result;
}

/** CPU-processed maps shared across color-only selection changes (gold↔silver, etc.). */
const cpuProcessTexCache = new Map<string, THREE.CanvasTexture>();

/** Fingerprint Tile/Off/Rot/wrap so Exposure/HSL re-bake when UV Transform changes. */
function processTextureUvFingerprint(tex: THREE.Texture): string {
  return [
    (tex.repeat?.x ?? 1).toFixed(5),
    (tex.repeat?.y ?? 1).toFixed(5),
    (tex.offset?.x ?? 0).toFixed(5),
    (tex.offset?.y ?? 0).toFixed(5),
    (tex.rotation ?? 0).toFixed(5),
    (tex.center?.x ?? 0).toFixed(5),
    (tex.center?.y ?? 0).toFixed(5),
    tex.wrapS,
    tex.wrapT,
  ].join(',');
}

/** Bake source with identity UV — pixels stay untiled; GPU matrix carries UV Transform. */
function textureWithIdentityUvForBake(tex: THREE.Texture): THREE.Texture {
  const t = cloneTextureLight(tex);
  if (tex.image && !t.image) t.image = tex.image;
  t.repeat.set(1, 1);
  t.offset.set(0, 0);
  t.center.set(0, 0);
  t.rotation = 0;
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  // cloneTextureLight shallow-copies userData. A stale __c3dBakePending copy would make
  // textureCpuPixelsSettled(bakeSrc) hang forever after the real input finishes.
  delete (t.userData as Record<string, unknown>).__c3dBakePending;
  delete (t.userData as Record<string, unknown>).__c3dCpuProcessCached;
  delete (t.userData as Record<string, unknown>).__c3dProcessFn;
  delete (t.userData as Record<string, unknown>).__c3dProcessSource;
  delete (t.userData as Record<string, unknown>).__c3dProcessInput;
  delete (t.userData as Record<string, unknown>).__c3dSyncFinish;
  commitTextureUV(t);
  return t;
}

function resetTextureUvIdentity(tex: THREE.Texture): void {
  tex.repeat.set(1, 1);
  tex.offset.set(0, 0);
  tex.center.set(0, 0);
  tex.rotation = 0;
  commitTextureUV(tex);
}

type ProcessTextureOptions = {
  /**
   * When true, bake from untransformed source pixels and copy Tile/Offset/Rot/wrap onto the
   * result so UV Transform matches Image/Normal. When false (legacy), UV is rasterized into
   * the canvas and the result uses identity UV — only safe if every PBR port shares that bake.
   */
  preserveSourceUv?: boolean;
};

/** Color/tonal CPU nodes must keep GPU UV in sync with sibling image maps. */
const PRESERVE_SOURCE_UV: ProcessTextureOptions = { preserveSourceUv: true };

function stampProcessedSolidColor(
  source: THREE.Texture,
  dest: THREE.Texture,
  fn: (r: number, g: number, b: number, a: number) => [number, number, number, number],
): void {
  const src = (source.userData as Record<string, unknown> | undefined)?.__c3dSolidColor;
  if (typeof src !== 'string') return;
  const parsed = rgbFromColorString(src);
  if (!parsed) return;
  const [r, g, b] = fn(parsed.r, parsed.g, parsed.b, 255);
  (dest.userData as any).__c3dSolidColor = formatHexRgb(r, g, b);
}

function processTexture(
  tex: THREE.Texture,
  fn: (r: number, g: number, b: number, a: number) => [number, number, number, number],
  recipe: string,
  opts?: ProcessTextureOptions,
): THREE.CanvasTexture {
  const dims = graphCpuBakeDimsFor(tex);
  const preserveSourceUv = !!opts?.preserveSourceUv;
  const bakeSrc = preserveSourceUv ? textureWithIdentityUvForBake(tex) : tex;
  const cacheKey = preserveSourceUv
    ? `${processTextureSourceKey(tex)}::${recipe}::rawUv::sz${dims.w}x${dims.h}`
    : `${processTextureSourceKey(tex)}::${recipe}::uv:${processTextureUvFingerprint(tex)}::sz${dims.w}x${dims.h}`;
  const cached = cpuProcessTexCache.get(cacheKey);
  if (cached) {
    if (preserveSourceUv) {
      adoptTextureUv(cached, tex);
      (cached.userData as any).__c3dProcessUvAdoptFrom = tex;
    }
    (cached.userData as any).__c3dProcessInput = tex;
    stampProcessedSolidColor(tex, cached, fn);
    (cached.userData as any).__c3dProcessCacheKey = cacheKey;
    // Rebake only when the sampled source actually got newer pixels (placeholder → real).
    // `!==` on a new UV clone's epoch retriggered the live canvas bake every Wood select.
    const kick = (cached.userData as any).__c3dChunkedKick as (() => void) | undefined;
    if (
      kick &&
      textureCpuPixelsSettled(tex) &&
      !((cached.userData as any).__c3dBakePending)
    ) {
      const bakedFrom = ((cached.userData as any).__c3dBakedFromEpoch as number | undefined) ?? 0;
      const srcEpoch = sourceContentEpoch(tex);
      if (srcEpoch > bakedFrom) {
        (cached.userData as any).__c3dBakePending = true;
        kick();
      }
    }
    return cached;
  }

  const canvas = document.createElement('canvas');
  canvas.width = dims.w;
  canvas.height = dims.h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  // Placeholder until async bake finishes — MUST NOT replace a finished map on metal tint swaps.
  // Caching this instance means yellow/silver evals share one texture that fills in-place.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, dims.w, dims.h);
  const result = new THREE.CanvasTexture(canvas);
  result.colorSpace = tex.colorSpace;
  result.flipY = false;
  if (preserveSourceUv) {
    result.wrapS = tex.wrapS;
    result.wrapT = tex.wrapT;
    adoptTextureUv(result, tex);
  } else {
    // UV Transform is rasterized into pixels — shader must sample at identity or tiles double.
    result.wrapS = THREE.ClampToEdgeWrapping;
    result.wrapT = THREE.ClampToEdgeWrapping;
    resetTextureUvIdentity(result);
  }
  configureStableTextureSampling(result);
  (result.userData as any).__c3dBakePending = true;
  // Stand-in for display while the white canvas fills — NOT `__c3dBakeSource`.
  // Pointing bakeSource at the raw image made textureHasUploadableData clear pending
  // early and assigned solid-white placeholders as Wood albedo.
  (result.userData as any).__c3dBakeStandIn = tex;
  (result.userData as any).__c3dCpuProcessCached = true;
  // So live-mesh apply can sync-finish Exposure/HSL without re-running graph eval.
  (result.userData as any).__c3dProcessFn = fn;
  (result.userData as any).__c3dProcessSource = bakeSrc;
  // Original graph input — settle checks must use this, not the identity-UV bake clone.
  (result.userData as any).__c3dProcessInput = tex;
  if (preserveSourceUv) {
    (result.userData as any).__c3dProcessUvAdoptFrom = tex;
  }
  stampProcessedSolidColor(tex, result, fn);
  (result.userData as any).__c3dProcessCacheKey = cacheKey;
  cpuProcessTexCache.set(cacheKey, result);

  async function draw(shouldCancel: () => boolean): Promise<boolean> {
    // Chained CPU nodes (Desaturate→Brightness): never sample an upstream white placeholder.
    // HTMLCanvasElement is always "image ready", so pending must be checked explicitly.
    adoptCloneSourceImage(tex);
    if (!textureCpuPixelsSettled(tex)) return false;
    // Identity-UV bake clones are often minted before TextureLoader fills `image`.
    if (bakeSrc !== tex) {
      adoptCloneSourceImage(bakeSrc);
      if (tex.image && !bakeSrc.image) bakeSrc.image = tex.image;
    }
    const bakeDims = graphCpuBakeDimsFor(tex);
    const liveCtx = ensureCpuBakeCanvasSize(canvas, bakeDims.w, bakeDims.h);
    if (!liveCtx) return false;
    const imgData = await bakeTextureToImageData(bakeSrc, bakeDims.w, bakeDims.h, liveCtx, shouldCancel);
    if (!imgData || shouldCancel()) return false;
    const d = imgData.data;
    const w = bakeDims.w;
    const done = await runRowsBudgeted(
      bakeDims.h,
      (y) => {
        const end = (y + 1) * w * 4;
        for (let i = y * w * 4; i < end; i += 4) {
          const [r, g, b, a] = fn(d[i]!, d[i + 1]!, d[i + 2]!, d[i + 3]!);
          d[i] = clamp255(r);
          d[i + 1] = clamp255(g);
          d[i + 2] = clamp255(b);
          d[i + 3] = clamp255(a);
        }
      },
      shouldCancel,
    );
    if (!done) return false;
    liveCtx.putImageData(imgData, 0, 0);
    delete (result.userData as any).__c3dBakePending;
    delete (result.userData as any).__c3dBakeStandIn;
    bumpCpuContentEpoch(result);
    (result.userData as any).__c3dBakedFromEpoch = sourceContentEpoch(
      ((result.userData as any).__c3dProcessInput as THREE.Texture | undefined) ?? tex,
    );
    if (preserveSourceUv) {
      const adoptFrom = (result.userData as any).__c3dProcessUvAdoptFrom as THREE.Texture | undefined;
      // Same guard as trySyncFinish: never stamp identity UV onto a UV-transformed clone.
      const looksLikeUvClone =
        Math.abs((result.repeat?.x ?? 1) - 1) > 1e-5 ||
        Math.abs((result.repeat?.y ?? 1) - 1) > 1e-5 ||
        Math.abs(result.offset?.x ?? 0) > 1e-5 ||
        Math.abs(result.offset?.y ?? 0) > 1e-5 ||
        Math.abs(result.rotation ?? 0) > 1e-5;
      if (!looksLikeUvClone) {
        if (adoptFrom) adoptTextureUv(result, adoptFrom);
      }
    } else {
      resetTextureUvIdentity(result);
    }
    result.needsUpdate = true;
    return true;
  }

  // Source CanvasTextures may re-render (e.g. after a custom font loads), so the
  // installer keeps watching the source version for a while after the first draw.
  // Include upstream content epoch so Desaturate→Brightness re-bakes after the
  // upstream placeholder is filled (player cold start used to cache solid white).
  installChunkedDraw({
    sourceReady: () => textureCpuPixelsSettled(tex),
    currentKey: () =>
      `${cpuContentEpoch(tex)}:${tex.version}:${preserveSourceUv ? 'raw' : processTextureUvFingerprint(tex)}`,
    draw,
    attachKick: (kick) => {
      (result.userData as any).__c3dChunkedKick = kick;
    },
  });

  return result;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

function hue2rgb(p: number, q: number, t: number): number {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2rgb(p, q, h + 1 / 3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1 / 3)];
}

function drawTexToScratch(
  ctx: CanvasRenderingContext2D,
  tex: THREE.Texture | null,
  w: number,
  h: number,
  fillWhiteIfMissing: boolean,
): void {
  if (fillWhiteIfMissing) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
  } else {
    ctx.clearRect(0, 0, w, h);
  }
  if (tex && textureCpuPixelsSettled(tex)) {
    ctx.drawImage(tex.image as CanvasImageSource, 0, 0, w, h);
  }
}

function blendRgb(
  br: number,
  bg: number,
  bb: number,
  lr: number,
  lg: number,
  lb: number,
  mode: string,
): [number, number, number] {
  switch (mode) {
    case 'multiply':
      return [(br * lr) / 255, (bg * lg) / 255, (bb * lb) / 255];
    case 'overlay': {
      const ov = (b: number, l: number) =>
        b < 128 ? (2 * b * l) / 255 : 255 - (2 * (255 - b) * (255 - l)) / 255;
      return [ov(br, lr), ov(bg, lg), ov(bb, lb)];
    }
    case 'add':
      return [
        Math.min(255, br + lr),
        Math.min(255, bg + lg),
        Math.min(255, bb + lb),
      ];
    case 'alpha':
    default:
      return [lr, lg, lb];
  }
}

/** Per-pixel blend: factor = mask luminance × mask alpha × opacity. */
function blendTexturesMasked(
  base: THREE.Texture | null,
  layer: THREE.Texture | null,
  mask: THREE.Texture,
  mode: string,
  opacity: number,
): THREE.CanvasTexture {
  const dims = graphCpuBakeDimsForMany([base, layer, mask]);
  const canvas = document.createElement('canvas');
  canvas.width = dims.w;
  canvas.height = dims.h;
  const outCtx = canvas.getContext('2d', { willReadFrequently: true })!;
  const scratch = document.createElement('canvas');
  scratch.width = dims.w;
  scratch.height = dims.h;
  const sctx = scratch.getContext('2d', { willReadFrequently: true })!;

  const result = new THREE.CanvasTexture(canvas);
  result.colorSpace = THREE.SRGBColorSpace;
  result.flipY = false;
  result.wrapS = (base ?? layer ?? mask).wrapS;
  result.wrapT = (base ?? layer ?? mask).wrapT;
  configureStableTextureSampling(result);

  outCtx.fillStyle = '#ffffff';
  outCtx.fillRect(0, 0, dims.w, dims.h);
  (result.userData as any).__c3dBakePending = true;
  (result.userData as any).__c3dCpuProcessCached = true;

  const inputsReady = () => {
    if (base) promotePendingCpuProcessTexture(base);
    if (layer) promotePendingCpuProcessTexture(layer);
    if (!isImageReady(mask.image)) return false;
    if (base && !textureCpuPixelsSettled(base)) return false;
    if (layer && !textureCpuPixelsSettled(layer)) return false;
    return !!(base || layer);
  };

  const syncFinish = (): boolean => {
    if (!inputsReady()) return false;
    const want = graphCpuBakeDimsForMany([base, layer, mask]);
    const live = ensureCpuBakeCanvasSize(canvas, want.w, want.h);
    if (!live) return false;
    if (scratch.width !== want.w || scratch.height !== want.h) {
      scratch.width = want.w;
      scratch.height = want.h;
    }

    drawTexToScratch(sctx, base, want.w, want.h, true);
    const baseData = sctx.getImageData(0, 0, want.w, want.h);
    drawTexToScratch(sctx, layer, want.w, want.h, false);
    const layerData = sctx.getImageData(0, 0, want.w, want.h);
    drawTexToScratch(sctx, mask, want.w, want.h, false);
    const maskData = sctx.getImageData(0, 0, want.w, want.h);

    const bd = baseData.data;
    const ld = layerData.data;
    const md = maskData.data;
    const haveLayer = textureCpuPixelsSettled(layer);
    const outImg = live.createImageData(want.w, want.h);
    const od = outImg.data;
    const op = Math.min(1, Math.max(0, opacity));

    for (let i = 0; i < od.length; i += 4) {
      const br = bd[i]!;
      const bg = bd[i + 1]!;
      const bb = bd[i + 2]!;
      const lr = haveLayer ? ld[i]! : br;
      const lg = haveLayer ? ld[i + 1]! : bg;
      const lb = haveLayer ? ld[i + 2]! : bb;
      const lum = (0.299 * md[i]! + 0.587 * md[i + 1]! + 0.114 * md[i + 2]!) / 255;
      const ma = md[i + 3]! / 255;
      const f = Math.min(1, Math.max(0, lum * ma * op));
      const [xr, xg, xb] = blendRgb(br, bg, bb, lr, lg, lb, mode);
      od[i] = Math.round(br * (1 - f) + xr * f);
      od[i + 1] = Math.round(bg * (1 - f) + xg * f);
      od[i + 2] = Math.round(bb * (1 - f) + xb * f);
      od[i + 3] = 255;
    }
    live.putImageData(outImg, 0, 0);
    delete (result.userData as any).__c3dBakePending;
    delete (result.userData as any).__c3dBakeStandIn;
    result.needsUpdate = true;
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('scene-texture-ready'));
    }
    return true;
  };

  (result.userData as any).__c3dSyncFinish = syncFinish;
  if (base && textureCpuPixelsSettled(base)) {
    (result.userData as any).__c3dBakeStandIn = base;
  } else if (layer && textureCpuPixelsSettled(layer)) {
    (result.userData as any).__c3dBakeStandIn = layer;
  }

  if (!syncFinish()) {
    installChunkedDraw({
      sourceReady: inputsReady,
      currentKey: () =>
        `${base?.version ?? 'x'}:${layer?.version ?? 'x'}:${mask.version}:${(base?.userData as any)?.__c3dBakePending ? 1 : 0}:${(layer?.userData as any)?.__c3dBakePending ? 1 : 0}`,
      draw: async (shouldCancel) => {
        if (shouldCancel()) return false;
        return syncFinish();
      },
    });
  }

  return result;
}

/**
 * Composite two textures. MUST NOT snapshot pending CPU bakes (HSL/Exposure white
 * placeholders) — Wood Color→Tex × HSL→Blend used to freeze that white forever.
 */
function blendTextures(
  base: THREE.Texture | null,
  layer: THREE.Texture | null,
  mode: string,
  opacity: number,
): THREE.CanvasTexture | null {
  if (!base && !layer) return null;

  const dims = graphCpuBakeDimsForMany([base, layer]);
  const canvas = document.createElement('canvas');
  canvas.width = dims.w;
  canvas.height = dims.h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

  const result = new THREE.CanvasTexture(canvas);
  result.colorSpace = THREE.SRGBColorSpace;
  result.flipY = false;
  if (base) {
    result.wrapS = base.wrapS;
    result.wrapT = base.wrapT;
  } else if (layer) {
    result.wrapS = layer.wrapS;
    result.wrapT = layer.wrapT;
  }
  configureStableTextureSampling(result);

  // Placeholder until inputs settle — flagged pending so GlbModel never assigns it as albedo.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, dims.w, dims.h);
  (result.userData as any).__c3dBakePending = true;
  (result.userData as any).__c3dCpuProcessCached = true;

  const inputsReady = () => {
    if (base) promotePendingCpuProcessTexture(base);
    if (layer) promotePendingCpuProcessTexture(layer);
    if (base && !textureCpuPixelsSettled(base)) return false;
    if (layer && !textureCpuPixelsSettled(layer)) return false;
    return true;
  };

  const syncFinish = (): boolean => {
    if (!inputsReady()) return false;
    const want = graphCpuBakeDimsForMany([base, layer]);
    const live = ensureCpuBakeCanvasSize(canvas, want.w, want.h);
    if (!live) return false;

    live.fillStyle = '#ffffff';
    live.fillRect(0, 0, want.w, want.h);

    if (base && textureCpuPixelsSettled(base)) {
      live.drawImage(base.image as CanvasImageSource, 0, 0, want.w, want.h);
    }

    if (layer && textureCpuPixelsSettled(layer)) {
      live.save();
      live.globalAlpha = opacity;
      switch (mode) {
        case 'multiply':
          live.globalCompositeOperation = 'multiply';
          break;
        case 'overlay':
          live.globalCompositeOperation = 'overlay';
          break;
        case 'add':
          live.globalCompositeOperation = 'lighter';
          break;
        default:
          live.globalCompositeOperation = 'source-over';
      }
      live.drawImage(layer.image as CanvasImageSource, 0, 0, want.w, want.h);
      live.restore();
    }

    delete (result.userData as any).__c3dBakePending;
    delete (result.userData as any).__c3dBakeStandIn;
    result.needsUpdate = true;
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('scene-texture-ready'));
    }
    return true;
  };

  (result.userData as any).__c3dSyncFinish = syncFinish;
  // Prefer settled base (Color→Tex brown) as stand-in metadata only — never assign raw stand-in.
  if (base && textureCpuPixelsSettled(base)) {
    (result.userData as any).__c3dBakeStandIn = base;
  }

  if (!syncFinish()) {
    installChunkedDraw({
      sourceReady: inputsReady,
      currentKey: () =>
        `${base?.version ?? 'x'}:${layer?.version ?? 'x'}:${mode}:${opacity}:${(base?.userData as any)?.__c3dBakePending ? 1 : 0}:${(layer?.userData as any)?.__c3dBakePending ? 1 : 0}`,
      draw: async (shouldCancel) => {
        if (shouldCancel()) return false;
        return syncFinish();
      },
    });
  }

  return result;
}

function processTextureToNormalMap(
  tex: THREE.Texture,
  strength: number,
  invertY: boolean,
): THREE.CanvasTexture {
  const dims = graphCpuBakeDimsFor(tex);
  const bw = dims.w;
  const bh = dims.h;
  const canvas = document.createElement('canvas');
  canvas.width = bw;
  canvas.height = bh;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  const result = new THREE.CanvasTexture(canvas);
  result.colorSpace = THREE.LinearSRGBColorSpace;
  result.flipY = false;
  result.wrapS = tex.wrapS;
  result.wrapT = tex.wrapT;
  configureStableTextureSampling(result);

  ctx.fillStyle = '#8080ff';
  ctx.fillRect(0, 0, bw, bh);

  function heightAt(d: Uint8ClampedArray, px: number, py: number, w: number): number {
    const j = (py * w + px) * 4;
    return (0.299 * d[j] + 0.587 * d[j + 1] + 0.114 * d[j + 2]) / 255;
  }

  async function draw(shouldCancel: () => boolean): Promise<boolean> {
    if (!isImageReady(tex.image)) return false;
    tex.updateMatrix();
    const ySign = invertY ? -1 : 1;
    const s = Math.max(0, strength);
    const du = 1 / bw;
    const dv = 1 / bh;

    if (textureNeedsUvAwareSampling(tex)) {
      const raster = getTextureRasterForSampling(tex);
      if (raster) {
        const { data: srcData, w: iw, h: ih } = raster;
        const sampleLum = (uMesh: number, vMesh: number) => {
          _processTexUvScratch.set(uMesh, vMesh);
          tex.transformUv(_processTexUvScratch);
          const [r, g, b] = sampleTextureBilinear(
            srcData,
            iw,
            ih,
            _processTexUvScratch.x,
            _processTexUvScratch.y,
          );
          return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        };
        const out = ctx.createImageData(bw, bh);
        const d = out.data;
        const done = await runRowsBudgeted(
          bh,
          (y) => {
            for (let x = 0; x < bw; x++) {
              const o = (y * bw + x) * 4;
              if (x === 0 || y === 0 || x === bw - 1 || y === bh - 1) {
                d[o] = 128;
                d[o + 1] = 128;
                d[o + 2] = 255;
                d[o + 3] = 255;
                continue;
              }
              const u0 = (x + 0.5) / bw;
              const v0 = 1 - (y + 0.5) / bh;
              const dx = (sampleLum(u0 + du, v0) - sampleLum(u0 - du, v0)) * 0.5 * s;
              const dy = (sampleLum(u0, v0 - dv) - sampleLum(u0, v0 + dv)) * 0.5 * s * ySign;
              let nx = -dx;
              let ny = -dy;
              let nz = 1;
              const len = Math.hypot(nx, ny, nz) || 1;
              nx /= len;
              ny /= len;
              nz /= len;
              d[o] = Math.round(nx * 127.5 + 127.5);
              d[o + 1] = Math.round(ny * 127.5 + 127.5);
              d[o + 2] = Math.round(nz * 127.5 + 127.5);
              d[o + 3] = 255;
            }
          },
          shouldCancel,
        );
        if (!done) return false;
        ctx.putImageData(out, 0, 0);
        result.needsUpdate = true;
        return true;
      }
    }

    const src = blitTextureToImageData(tex, bw, bh);
    if (!src) return false;
    const d = src.data;

    const done = await runRowsBudgeted(
      bh,
      (y) => {
        for (let x = 0; x < bw; x++) {
          const o = (y * bw + x) * 4;
          if (x === 0 || y === 0 || x === bw - 1 || y === bh - 1) {
            d[o] = 128;
            d[o + 1] = 128;
            d[o + 2] = 255;
            d[o + 3] = 255;
            continue;
          }
          const dx = (heightAt(d, x + 1, y, bw) - heightAt(d, x - 1, y, bw)) * 0.5 * s;
          const dy =
            (heightAt(d, x, y + 1, bw) - heightAt(d, x, y - 1, bw)) * 0.5 * s * ySign;
          let nx = -dx;
          let ny = -dy;
          let nz = 1;
          const len = Math.hypot(nx, ny, nz) || 1;
          nx /= len;
          ny /= len;
          nz /= len;
          d[o] = Math.round(nx * 127.5 + 127.5);
          d[o + 1] = Math.round(ny * 127.5 + 127.5);
          d[o + 2] = Math.round(nz * 127.5 + 127.5);
          d[o + 3] = 255;
        }
      },
      shouldCancel,
    );
    if (!done) return false;
    ctx.putImageData(src, 0, 0);
    result.needsUpdate = true;
    return true;
  }

  installChunkedDraw({
    sourceReady: () => isImageReady(tex.image),
    currentKey: () => tex.version,
    draw,
  });

  return result;
}

/** Collect static `imageTexture` URLs from a material graph (for idle prefetch). */
export function collectMaterialGraphImageUrls(graph: unknown): string[] {
  const g = graph as { nodes?: Array<{ type?: string; data?: Record<string, unknown> }> } | null | undefined;
  const out = new Set<string>();
  for (const n of g?.nodes ?? []) {
    if (n?.type === 'imageTexture') {
      const u = n.data?.url;
      if (typeof u === 'string' && u.length > 0) out.add(u);
    }
  }
  return [...out];
}

/**
 * Prime the graph texture loader cache (decode + GPU upload). Call from idle time so the first
 * pick of each color/metal/material option does not hitch the main thread.
 * Prefetch is untracked so it never holds the first-reveal loader open.
 */
export function warmMaterialGraphTextureCache(urls: Iterable<string>): void {
  for (const url of urls) {
    if (typeof url !== 'string' || url.length === 0) continue;
    try {
      loadTex(url, false, { track: false });
      loadTex(url, true, { track: false });
    } catch {
      /* ignore */
    }
  }
}

/** Attribute keys that affect `relevantSelectionsKey` / graph evaluation. */
export function listMaterialGraphAttributeKeys(graph: MaterialGraphData): string[] {
  const keys = new Set<string>();
  for (const node of graph.nodes) {
    if (node.type === 'attributeInput') {
      const k = node.data.attributeKey as string;
      if (k) keys.add(k);
    }
    if (node.type === 'colorToTexture' || node.type === 'colorAdjust') {
      const k = String((node.data.attributeKey as string) || '').trim();
      if (k) keys.add(k);
    }
  }
  return [...keys].sort();
}

/**
 * Evaluate and cache the material graph for many attribute combinations (Cartesian product of
 * catalog options for keys wired in the graph). Removes first-click hitches when only swatches change.
 * Caps work with `maxVariants` to avoid huge graphs × huge option sets.
 */
export function warmMaterialGraphEvalVariants(
  graph: MaterialGraphData,
  attributeDefs: AttributeDef[],
  baseSelections: Record<string, unknown>,
  evalContext: MaterialGraphEvalContext | undefined,
  maxVariants = 384,
): number {
  const gKeys = listMaterialGraphAttributeKeys(graph);
  if (gKeys.length === 0) return 0;

  const valueChoices: string[][] = gKeys.map((k) => {
    const def = attributeDefs.find((a) => a.key === k);
    const opts = def?.options?.map((o) => String(o.key)) ?? [];
    if (opts.length > 0) return opts;
    if (def?.defaultValue != null && String(def.defaultValue).length > 0) return [String(def.defaultValue)];
    return [''];
  });

  let count = 0;
  const recur = (i: number, partial: Record<string, unknown>) => {
    if (count >= maxVariants) return;
    if (i >= gKeys.length) {
      const merged = { ...baseSelections, ...partial };
      try {
        evaluateMaterialGraph(graph, merged, attributeDefs, evalContext);
      } catch {
        /* ignore */
      }
      count++;
      return;
    }
    const key = gKeys[i]!;
    for (const v of valueChoices[i]!) {
      if (count >= maxVariants) return;
      recur(i + 1, { ...partial, [key]: v });
    }
  };
  recur(0, {});
  return count;
}

export function disposeMaterialGraphResult(result: EvalResult): void {
  for (const tex of result.textures) {
    // Shared CPU process cache — disposing here would blank AO maps on the next metal tint.
    if ((tex.userData as any)?.__c3dCpuProcessCached) continue;
    tex.dispose();
  }
}

registerPlayerTextureCacheClearHook(() => {
  for (const result of evalCache.values()) {
    disposeMaterialGraphResult(result);
  }
  evalCache.clear();
  for (const tex of texCache.values()) {
    tex.dispose();
  }
  texCache.clear();
  for (const tex of cpuProcessTexCache.values()) {
    tex.dispose();
  }
  cpuProcessTexCache.clear();
});

registerAssetUrlTextureClearHook((oldUrl) => {
  disposeTexturesInMapByUrl(texCache, oldUrl);
});
